// ═══════════════════════════════════════════════════════════════════
// plazaFolk3D.jsx — THE PEOPLE OF THE PLAZA (Plaza 3D, Ship 1)
// ═══════════════════════════════════════════════════════════════════
// V'ael · Willy · V'yen · Venus · the Aenarchist (the player, m/f).
//
// ⭐ TRANSLATED, NOT INVENTED. Every body here is read off its real art:
//   V'ael   → `vael.webp`  (silver updo, curled ram horns, the crescent
//             on her brow, green eyes, the smirk, black robe with the
//             white collar, the cross, boots, the thin tufted tail)
//   Willy   → `willy.webp` (mossy green, huge brown eyes, brown vest
//             over a grey shirt, patched shorts, bare feet, THE BOOK)
//   V'yen   → `plaza_1.webp` (lavender skin, long white hair, gold
//             jewels, and the serpent coil sweeping down-left — the
//             coil is HER, not the stall's)
//   Venus   → `plaza_1.webp` (the lioness: mane, round ears, glasses,
//             cream dress, the tail, two packs held up)
//   player  → `player_{m,f}_down_a.webp` (m: white swept hair, beard,
//             green eyes, black coat with gold work, white shirt; f:
//             long white hair with the braid crown, violet eyes, purple
//             robe with the gold tabard, black sash, boots)
//
// THE RECIPE (ie-being-models): no boxes on a body — spheres, capsules,
// cones; the face is PAINTED on a sphere cap (one 128² canvas per
// face); coats are painted canvases; hair, horns, tails and coils are
// bead-chains along curves; every seeded thing is baked in useMemo and
// nothing calls a PRNG during render; idle life is one useFrame per
// body, sin-driven, fixed phases; auras are emissive — ZERO lights.
//
// THE RIG. Every limb hangs from a named group at its joint —
// rig_head · rig_armL · rig_armR · rig_legL · rig_legR · rig_tail —
// sitting at the joint with identity rotation (the kit's zero-transform
// law), so the one pose loop in <PersonBody> drives every person here
// and any person added later.
//
// CONTRACT: a body renders at the origin, standing on y=0, facing +z
// (Plaza "down" — toward the camera) at yaw 0. `spec.h` is its height
// to the crown in world units (one tile = one unit).
// ═══════════════════════════════════════════════════════════════════
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { mulberry32, canvasTex } from "./ieBeingModels";
import { VaelSculpted } from "./vaelModel3D";
import { LyraeiSculpted } from "./lyraeiModel3D";
import { LaostreSculpted } from "./laostreModel3D";
import { WillieSculpted, WillieForgeSculpted } from "./willieModel3D"; // Willie's sculpted body (caP'n, 2026-09-16, THE PLAZA UPDATE #1)
import { VyenSculpted } from "./vyenModel3D"; // V'yen's sculpted body (caP'n, 2026-09-16, THE PLAZA UPDATE #2)
import { VenusSculpted } from "./venusModel3D"; // Venus's sculpted body (caP'n, 2026-09-16, THE PLAZA UPDATE #3)
import { failSafe } from "./sculptGate"; // THE FAIL-SAFE LAW — painted bodies stand in when the Plaza can't keep up // Laostre's sculpted body (caP'n, 2026-09-16) // Lyraei's sculpted body (caP'n, 2026-09-16) // V'ael's sculpted body (caP'n, 2026-09-16)

// ONE DRAW CALL PER BEAD-BODY. A horn is 45 spheres and a coil is 231;
// nine stone rams on the fountain made 810 draw calls of horn alone,
// which is more than the whole room had at Ship 1 (478). So a run of
// beads that never moves relative to itself is merged into a single
// geometry, and the material is applied once. Memoised by the caller.
function mergeBeads(beads, ws = 10, hs = 8) {
  const parts = beads.map((b) => { const g = new THREE.SphereGeometry(b.r, ws, hs); g.translate(b.p[0], b.p[1], b.p[2]); return g; });
  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  return merged;
}

export const PERSON_H = 1.3;   // the Aenarchist, crown height — the room's scale reference

// ═══════════════════════════════════════════════════════════════════
// TEXTURES
// ═══════════════════════════════════════════════════════════════════
// The painted face. Transparent canvas; the skin shows through. Knobs
// for everything a plaza face needs.
export function faceTex(key, o) {
  return canvasTex("plaza3d:face:" + key, 128, 128, (g, w, h) => {
    g.clearRect(0, 0, w, h);
    const eyeY = 58, ex = 22;
    // brow shadow — this is what makes a sphere read as a head
    const bs = g.createLinearGradient(0, 30, 0, 70);
    bs.addColorStop(0, "rgba(0,0,0,0)"); bs.addColorStop(0.4, "rgba(0,0,0,0.10)"); bs.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = bs; g.fillRect(0, 30, w, 40);
    // eyes
    const eyeW = o.eyeW || 13, eyeH = o.eyeH || 9;
    for (const s of [-1, 1]) {
      const x = 64 + s * ex;
      g.fillStyle = "#f4f0ea"; g.beginPath(); g.ellipse(x, eyeY, eyeW, eyeH, 0, 0, Math.PI * 2); g.fill();
      const ir = g.createRadialGradient(x, eyeY, 1, x, eyeY, eyeH * 0.9);
      ir.addColorStop(0, o.eyeHot || "#fff"); ir.addColorStop(0.35, o.eye || "#3a7a4a"); ir.addColorStop(1, o.eyeRim || "#102010");
      g.fillStyle = ir; g.beginPath(); g.arc(x, eyeY, eyeH * 0.82, 0, Math.PI * 2); g.fill();
      g.fillStyle = "#0a0a0a"; g.beginPath(); g.ellipse(x, eyeY, eyeH * 0.36, eyeH * (o.slit ? 0.75 : 0.42), 0, 0, Math.PI * 2); g.fill();
      g.fillStyle = "rgba(255,255,255,0.85)"; g.beginPath(); g.arc(x - 3, eyeY - 3, 2.2, 0, Math.PI * 2); g.fill();
      // lid line
      g.strokeStyle = o.lash || "#2a1a1a"; g.lineWidth = 2.2; g.beginPath(); g.ellipse(x, eyeY, eyeW, eyeH, 0, Math.PI * 1.05, Math.PI * 1.95); g.stroke();
      // brow
      g.strokeStyle = o.brow || "#3a2a20"; g.lineWidth = 3; g.lineCap = "round"; g.beginPath();
      const tilt = (o.browTilt || 0) * s;
      g.moveTo(x - eyeW * 0.9, eyeY - eyeH - 8 + tilt); g.quadraticCurveTo(x, eyeY - eyeH - 13, x + eyeW * 0.9, eyeY - eyeH - 8 - tilt); g.stroke();
    }
    // nose line
    g.strokeStyle = "rgba(0,0,0,0.28)"; g.lineWidth = 2; g.beginPath(); g.moveTo(64, 66); g.lineTo(61, 80); g.lineTo(66, 82); g.stroke();
    // mouth
    g.strokeStyle = o.lip || "#7a3a3a"; g.lineWidth = 2.6; g.beginPath();
    if (o.mouth === "smirk") { g.moveTo(52, 96); g.quadraticCurveTo(66, 104, 80, 92); }
    else if (o.mouth === "smile") { g.moveTo(52, 94); g.quadraticCurveTo(64, 104, 76, 94); }
    else if (o.mouth === "frown") { g.moveTo(54, 98); g.quadraticCurveTo(64, 93, 74, 98); }
    else { g.moveTo(55, 96); g.lineTo(73, 96); }
    g.stroke();
    if (o.teeth) { g.fillStyle = "#fff"; g.beginPath(); g.moveTo(56, 96); g.quadraticCurveTo(66, 102, 78, 93); g.lineTo(76, 96); g.quadraticCurveTo(66, 99, 58, 97); g.fill(); }
    // blush
    if (o.blush) { for (const s of [-1, 1]) { const bl = g.createRadialGradient(64 + s * 30, 78, 0, 64 + s * 30, 78, 14); bl.addColorStop(0, o.blush); bl.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = bl; g.fillRect(64 + s * 30 - 14, 64, 28, 28); } }
    // beard
    if (o.beard) {
      g.fillStyle = o.beard; g.beginPath(); g.moveTo(34, 86); g.quadraticCurveTo(64, 128, 94, 86); g.quadraticCurveTo(64, 100, 34, 86); g.fill();
      g.fillStyle = "rgba(0,0,0,0.25)"; g.fillRect(50, 100, 28, 3);
    }
    // the crescent on the brow (V'ael)
    if (o.crescent) {
      g.fillStyle = o.crescent; g.shadowColor = o.crescent; g.shadowBlur = 6;
      g.beginPath(); g.arc(64, 30, 7, 0, Math.PI * 2); g.fill();
      g.shadowBlur = 0; g.fillStyle = "rgba(0,0,0,0)"; g.globalCompositeOperation = "destination-out";
      g.beginPath(); g.arc(67, 28, 6, 0, Math.PI * 2); g.fill(); g.globalCompositeOperation = "source-over";
    }
    // glasses (Venus)
    if (o.glasses) {
      g.strokeStyle = o.glasses; g.lineWidth = 2.2;
      for (const s of [-1, 1]) { g.beginPath(); g.ellipse(64 + s * ex, eyeY, eyeW + 4, eyeH + 4, 0, 0, Math.PI * 2); g.stroke(); }
      g.beginPath(); g.moveTo(64 - ex + eyeW + 4, eyeY); g.lineTo(64 + ex - eyeW - 4, eyeY); g.stroke();
    }
  });
}

// A painted cloth: base, a woven grain, optional gold work at the hems.
export function clothTex(key, base, opts = {}) {
  return canvasTex("plaza3d:cloth:" + key, 256, 256, (g, w, h) => {
    const R = mulberry32(opts.seed || 7);
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 900; i++) { g.fillStyle = `rgba(255,255,255,${R() * 0.05})`; g.fillRect(R() * w, R() * h, 1, 3); g.fillStyle = `rgba(0,0,0,${R() * 0.08})`; g.fillRect(R() * w, R() * h, 3, 1); }
    if (opts.trim) {
      g.strokeStyle = opts.trim; g.lineWidth = 3; g.shadowColor = opts.trim; g.shadowBlur = 3;
      g.strokeRect(10, 10, w - 20, h - 20);
      // filigree — little curls along the hem
      for (let i = 0; i < 12; i++) { const x = 16 + i * 20; g.beginPath(); g.arc(x, h - 22, 6, Math.PI, Math.PI * 2.4); g.stroke(); g.beginPath(); g.arc(x, 22, 6, 0, Math.PI * 1.4); g.stroke(); }
    }
    if (opts.patches) {
      const cols = ["#4f8a8a", "#8a4a5a", "#7a7a4a"];
      for (let i = 0; i < 4; i++) { g.fillStyle = cols[i % 3]; const x = R() * w, y = R() * h; g.fillRect(x, y, 30 + R() * 30, 24 + R() * 20); g.strokeStyle = "rgba(0,0,0,0.5)"; g.setLineDash([3, 4]); g.strokeRect(x + 3, y + 3, 24, 18); g.setLineDash([]); }
    }
  });
}

// Skin with a little life in it (moss for Willy, scales for V'yen's coil).
export function skinTex(key, base, kind) {
  return canvasTex("plaza3d:skin:" + key, 256, 256, (g, w, h) => {
    const R = mulberry32(0x5c1);
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    if (kind === "moss") {
      for (let i = 0; i < 300; i++) { g.fillStyle = `rgba(${90 + R() * 60 | 0},${120 + R() * 60 | 0},${40 + R() * 30 | 0},${0.25 + R() * 0.4})`; g.beginPath(); g.arc(R() * w, R() * h, 2 + R() * 7, 0, Math.PI * 2); g.fill(); }
    } else if (kind === "scales") {
      for (let y = 0; y < h; y += 14) for (let x = ((y / 14) | 0) % 2 ? 7 : 0; x < w; x += 14) {
        g.fillStyle = `hsl(${262 + R() * 12}, ${30 + R() * 15}%, ${22 + R() * 14}%)`; g.beginPath(); g.arc(x, y, 8, 0, Math.PI); g.fill();
        g.strokeStyle = "rgba(200,170,255,0.25)"; g.lineWidth = 1; g.beginPath(); g.arc(x, y, 8, 0, Math.PI); g.stroke();
      }
    } else {
      for (let i = 0; i < 400; i++) { g.fillStyle = `rgba(255,255,255,${R() * 0.04})`; g.fillRect(R() * w, R() * h, 2, 2); }
    }
  });
}

// ═══════════════════════════════════════════════════════════════════
// PRIMITIVES
// ═══════════════════════════════════════════════════════════════════
// ⚠ `children` must be pulled out of the spread: an explicit JSX child
// (the geometry) REPLACES a spread `children` prop, and every limb was
// quietly rendering in the default white material until this was found
// in the review room (2026-09-16).
const Cap = ({ r, l, children, ...p }) => <mesh {...p}><capsuleGeometry args={[r, l, 4, 8]} />{children}</mesh>;

// A bead chain along a hand-tuned curve — hair strands, horns, tails,
// and V'yen's whole lower half.
export function BeadChain({ pts, r0, r1, mat, tipEmissive, seg = 8, bScale = null }) {
  const beads = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)));
    // beads overlap or the chain reads as a string of pearls: spacing is
    // ~0.7 of the thinner radius
    const n = Math.min(160, Math.max(4, Math.round(curve.getLength() / (Math.min(r0, r1) * 0.7))));
    return curve.getPoints(n).map((v, i) => ({ p: [v.x, v.y, v.z], r: r0 + (r1 - r0) * (i / n) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pts), r0, r1]);
  // one draw call for the run; the lit tip (if any) is its own mesh
  const geo = useMemo(() => {
    const body = tipEmissive ? beads.slice(0, -1) : beads;
    const parts = body.map((b) => { const g = new THREE.SphereGeometry(b.r, seg, 6); if (bScale) g.scale(bScale[0], bScale[1], bScale[2]); g.translate(b.p[0], b.p[1], b.p[2]); return g; });
    const m = mergeGeometries(parts, false); for (const g of parts) g.dispose(); return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beads, seg, !!tipEmissive, bScale && bScale.join(",")]);
  const tip = beads[beads.length - 1];
  return (
    <group>
      <mesh geometry={geo}>{mat}</mesh>
      {tipEmissive && <mesh position={tip.p} scale={bScale || undefined}><sphereGeometry args={[tip.r, seg, 6]} /><meshBasicMaterial color={tipEmissive} toneMapped={false} /></mesh>}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE PERSON — one parametric body, dressed by a spec.
// ═══════════════════════════════════════════════════════════════════
// spec = {
//   h            crown height
//   skin         hex (or { map })       headR   head radius as a fraction of h
//   face         faceTex options        hair    { color, style } style ∈ swept|long|updo|mossy|mane|none
//   top          { color, trim, tex }   coat    { color, trim, hem } → a long coat / dress skirt
//   sleeves      hex                    hands   hex
//   legs         hex                    boots   hex     (barefoot: boots = skin)
//   collar       hex (a white collar)   tail    { color, tuft }  a thin tail from the rear hip
//   coil         true → V'yen: no legs, the serpent coil instead
//   extras       (node) anything attached to the head group
//   held         { L: node, R: node } props in the hands
//   idle         "stand" | "read" | "present" | "ledger" | "lorde"
//   arms         "down" | "pockets" | "up"
// }
export function PersonBody({ spec, ctl }) {
  const root = useRef(); const rigs = useRef({});
  const phase = useRef(0); const yaw = useRef(0);
  const t0 = useMemo(() => (spec.phase ?? 0), [spec.phase]);
  const h = spec.h, headR = h * (spec.headR ?? 0.115);
  const shoulderY = h * 0.72, hipY = h * 0.44;
  const armL = h * 0.24, legL = h * 0.34;
  const face = useMemo(() => faceTex(spec.key, spec.face), [spec.key, spec.face]);
  const skinMat = useMemo(() => spec.skinMap ? { map: spec.skinMap } : { color: spec.skin }, [spec.skin, spec.skinMap]);
  const topMat = useMemo(() => spec.top?.tex ? { map: spec.top.tex } : { color: spec.top?.color || "#444" }, [spec.top]);
  const coatMat = useMemo(() => spec.coat?.tex ? { map: spec.coat.tex } : { color: spec.coat?.color || "#222" }, [spec.coat]);
  // ⚠ The base rotation is captured ONCE, on mount. A ref callback runs
  // again on every re-render, and by then the limb is posed — capturing
  // that as the base would walk the pose off a little every render.
  const regs = useMemo(() => {
    const m = {};
    for (const k of ["head", "armL", "armR", "legL", "legR", "tail"]) {
      m[k] = (el) => { if (el && !rigs.current[k]) rigs.current[k] = { o: el, bx: el.rotation.x, bz: el.rotation.z }; };
    }
    return m;
  }, []);
  const reg = (k) => regs[k];

  useFrame(({ clock }, dtRaw) => {
    const o = root.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const t = clock.getElapsedTime() + t0;
    const speed = ctl.speed || 0;
    // the walk phase advances with SPEED, never with time — no moonwalk
    phase.current += speed * dt * 9.5;
    const ph = phase.current;
    // facing — Plaza {x,y}, y=+1 is "down" toward the camera = +z
    const f = ctl.facing || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 11);
    o.rotation.y = yaw.current + (ctl.yawOffset || 0);
    // breath + walk bob
    const breath = 1 + Math.sin(t * 1.7) * 0.012;
    o.scale.set(1 / Math.sqrt(breath), breath, 1 / Math.sqrt(breath));
    o.position.y = speed ? Math.abs(Math.sin(ph)) * h * 0.03 : 0;
    const R = rigs.current;
    const set = (k, x, z) => { const r = R[k]; if (r) { r.o.rotation.x = r.bx + x; r.o.rotation.z = r.bz + z; } };
    let legs = 0, arms = 0, headX = 0, headY = 0, armLz = 0, armRz = 0, armLx = 0, armRx = 0;
    if (speed) { legs = Math.sin(ph) * 0.6; arms = -Math.sin(ph) * 0.45; o.rotation.x = 0.04; }
    else { o.rotation.x = 0; }
    switch (spec.idle) {
      case "read":     headX = 0.55 + Math.sin(t * 0.6) * 0.04; armLx = -1.3; armLz = 0.35; armRx = Math.sin(t * 1.1) > 0.9 ? -0.3 : 0; break;
      case "ledger":   headX = 0.35; armRx = -1.1 + Math.sin(t * 4) * 0.06; armRz = -0.3 + Math.sin(t * 4) * 0.05; armLx = -0.9; armLz = 0.3; headY = Math.sin(t * 0.5) * 0.15; break;
      case "present":  armLx = -1.9 + Math.sin(t * 1.3) * 0.08; armRx = -1.9 - Math.sin(t * 1.3) * 0.08; armLz = 0.5; armRz = -0.5; headY = Math.sin(t * 0.8) * 0.1; break;
      case "lorde":    armLz = 0.22; armRz = -0.22; headY = Math.sin(t * 0.45) * 0.28; headX = -0.05 + Math.sin(t * 0.9) * 0.03; break;
      default:         headY = Math.sin(t * 0.7) * 0.14; armLz = 0.08; armRz = -0.08;
    }
    if (spec.arms === "pockets" && !speed) { armLx = -0.55; armRx = -0.55; armLz = 0.25; armRz = -0.25; }
    set("legL", legs, 0); set("legR", -legs, 0);
    set("armL", arms + armLx, armLz); set("armR", -arms + armRx, armRz);
    const hd = R.head; if (hd) { hd.o.rotation.x = hd.bx + headX; hd.o.rotation.y = headY; }
    const tl = R.tail; if (tl) { tl.o.rotation.y = Math.sin(t * 1.9) * 0.35; tl.o.rotation.x = tl.bx + Math.sin(t * 2.7) * 0.12; }
    if (ctl.onFrame) ctl.onFrame(t, R, o);
  });

  const hairStyle = spec.hair?.style || "none", hairC = spec.hair?.color || "#222";
  const hairMat = <meshStandardMaterial color={hairC} roughness={0.85} />;
  const skinM = <meshStandardMaterial {...skinMat} roughness={0.75} />;
  const long = spec.hair?.long;   // strands to the shoulders
  const legsY = spec.coil ? null : hipY;

  return (
    <group ref={root}>
      {/* ground whisper */}
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[h * 0.24, 14]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      {spec.aura && (
        <mesh position={[0, h * 0.5, 0]}><sphereGeometry args={[h * 0.5, 14, 10]} /><meshBasicMaterial color={spec.aura} transparent opacity={0.07} side={THREE.BackSide} depthWrite={false} toneMapped={false} /></mesh>
      )}

      {/* ── LEGS or the COIL ── */}
      {legsY !== null && [-1, 1].map((s) => (
        <group key={s} name={s < 0 ? "rig_legL" : "rig_legR"} ref={reg(s < 0 ? "legL" : "legR")} position={[s * h * 0.075, hipY, 0]}>
          <Cap r={h * 0.05} l={legL - h * 0.1} position={[0, -legL / 2, 0]} castShadow><meshStandardMaterial color={spec.legs || "#222"} roughness={0.9} /></Cap>
          <mesh position={[0, -legL - h * 0.015, h * 0.03]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[h * 0.065, 10, 8]} /><meshStandardMaterial color={spec.boots || "#1a1a1a"} roughness={0.6} /></mesh>
        </group>
      ))}
      {spec.coil && <SerpentCoil h={h} hipY={hipY} spec={spec.coil} />}

      {/* ── HIPS + TORSO ── */}
      <mesh position={[0, hipY + h * 0.02, 0]} scale={[1.25, 0.8, 0.9]} castShadow><sphereGeometry args={[h * 0.11, 12, 10]} /><meshStandardMaterial {...(spec.coat ? coatMat : { color: spec.legs || "#222" })} roughness={0.9} /></mesh>
      <mesh position={[0, (hipY + shoulderY) / 2 + h * 0.01, 0]} scale={[1.5, 1.35, 0.95]} castShadow><sphereGeometry args={[h * 0.115, 14, 12]} /><meshStandardMaterial {...topMat} roughness={0.85} /></mesh>
      {/* the coat / dress skirt: a cone from the waist to the hem */}
      {spec.coat && (
        <mesh position={[0, hipY - (hipY - h * (spec.coat.hem ?? 0.12)) / 2 + h * 0.03, 0]} castShadow>
          <coneGeometry args={[h * 0.19, hipY - h * (spec.coat.hem ?? 0.12) + h * 0.06, 12, 1, true]} />
          <meshStandardMaterial {...coatMat} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>
      )}
      {spec.shirt && (
        <mesh position={[0, shoulderY - h * 0.07, h * 0.15]} rotation={[0.1, 0, 0]}><planeGeometry args={[h * 0.07, h * 0.16]} /><meshStandardMaterial color={spec.shirt} roughness={0.8} /></mesh>
      )}
      {spec.tabard && (
        <mesh position={[0, hipY - h * 0.05, h * 0.16]} rotation={[0.12, 0, 0]}><planeGeometry args={[h * 0.08, h * 0.4]} /><meshStandardMaterial color={spec.tabard} emissive={spec.tabard} emissiveIntensity={0.05} roughness={0.5} metalness={0.4} /></mesh>
      )}
      {spec.sash && <mesh position={[0, hipY + h * 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[h * 0.135, h * 0.022, 6, 16]} /><meshStandardMaterial color={spec.sash} roughness={0.9} /></mesh>}
      {spec.collar && <mesh position={[0, shoulderY + h * 0.03, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[h * 0.09, h * 0.03, 6, 14]} /><meshStandardMaterial color={spec.collar} roughness={0.7} /></mesh>}
      {spec.vest && (
        <mesh position={[0, (hipY + shoulderY) / 2, 0]} scale={[1.62, 1.3, 1.0]}><sphereGeometry args={[h * 0.115, 12, 10, Math.PI * 0.62, Math.PI * 1.76, 0.4, Math.PI * 0.62]} /><meshStandardMaterial color={spec.vest} roughness={0.95} side={THREE.DoubleSide} /></mesh>
      )}
      {spec.necklace && (
        <group position={[0, shoulderY - h * 0.05, h * 0.11]}>
          <mesh rotation={[0.2, 0, 0]}><torusGeometry args={[h * 0.06, h * 0.005, 4, 14, Math.PI]} /><meshStandardMaterial color="#cfd3dc" metalness={0.9} roughness={0.3} /></mesh>
          <mesh position={[0, -h * 0.06, 0]}><boxGeometry args={[h * 0.008, h * 0.05, h * 0.006]} /><meshStandardMaterial color="#e8ecf4" metalness={0.9} roughness={0.25} /></mesh>
          <mesh position={[0, -h * 0.05, 0]}><boxGeometry args={[h * 0.03, h * 0.008, h * 0.006]} /><meshStandardMaterial color="#e8ecf4" metalness={0.9} roughness={0.25} /></mesh>
        </group>
      )}

      {/* ── ARMS ── */}
      {[-1, 1].map((s) => (
        <group key={s} name={s < 0 ? "rig_armL" : "rig_armR"} ref={reg(s < 0 ? "armL" : "armR")} position={[s * h * 0.165, shoulderY, 0]}>
          <Cap r={h * 0.042} l={armL - h * 0.08} position={[0, -armL / 2, 0]} castShadow><meshStandardMaterial color={spec.sleeves || spec.top?.color || "#333"} roughness={0.9} /></Cap>
          <mesh position={[0, -armL - h * 0.01, 0]}><sphereGeometry args={[h * 0.046, 10, 8]} /><meshStandardMaterial color={spec.hands || spec.skin} roughness={0.75} /></mesh>
          {spec.held?.[s < 0 ? "L" : "R"] && <group position={[0, -armL - h * 0.02, 0]}>{spec.held[s < 0 ? "L" : "R"]}</group>}
        </group>
      ))}

      {/* ── HEAD ── */}
      <group name="rig_head" ref={reg("head")} position={[0, h * 0.78, 0]}>
        <mesh position={[0, -h * 0.01, 0]}><cylinderGeometry args={[h * 0.035, h * 0.045, h * 0.06, 8]} /><meshStandardMaterial {...skinMat} roughness={0.75} /></mesh>
        <mesh position={[0, headR * 0.95, 0]} castShadow><sphereGeometry args={[headR, 18, 14]} />{skinM}</mesh>
        {/* the painted face on a sphere cap, front (+z) */}
        {/* SphereGeometry: phi=π/2 is +z, so a cap centred there faces the
            camera at yaw 0; u runs -x→+x so the canvas is not mirrored. */}
        <mesh position={[0, headR * 0.95, 0]}>
          <sphereGeometry args={[headR * 1.012, 18, 14, Math.PI * 0.18, Math.PI * 0.64, Math.PI * 0.28, Math.PI * 0.5]} />
          <meshStandardMaterial map={face} transparent roughness={0.7} />
        </mesh>
        {/* ears */}
        {spec.ears !== "round" && spec.ears !== "none" && [-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.98, headR * 0.9, 0]} scale={[0.5, 1, 0.8]}><sphereGeometry args={[headR * 0.22, 8, 6]} />{skinM}</mesh>)}
        {spec.ears === "round" && [-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.75, headR * 1.7, -headR * 0.1]}><sphereGeometry args={[headR * 0.34, 10, 8]} /><meshStandardMaterial color={spec.hair?.color || "#8a5a2a"} roughness={0.9} /></mesh>)}
        {/* HAIR */}
        {hairStyle === "swept" && (
          <group>
            <mesh position={[0, headR * 1.15, -headR * 0.1]} scale={[1.05, 0.8, 1.05]}><sphereGeometry args={[headR * 1.02, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.55]} />{hairMat}</mesh>
            <mesh position={[headR * 0.3, headR * 1.75, headR * 0.35]} rotation={[0.5, 0, -0.4]} scale={[1.2, 0.7, 1]}><sphereGeometry args={[headR * 0.45, 10, 8]} />{hairMat}</mesh>
            <mesh position={[-headR * 0.25, headR * 1.7, headR * 0.2]} rotation={[0.3, 0, 0.5]} scale={[1.1, 0.6, 1]}><sphereGeometry args={[headR * 0.4, 10, 8]} />{hairMat}</mesh>
          </group>
        )}
        {hairStyle === "long" && (
          <group>
            <mesh position={[0, headR * 1.1, -headR * 0.15]} scale={[1.08, 0.9, 1.1]}><sphereGeometry args={[headR * 1.02, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.6]} />{hairMat}</mesh>
            {spec.hair.braid && <mesh position={[0, headR * 1.55, 0]} rotation={[Math.PI / 2 + 0.2, 0, 0]}><torusGeometry args={[headR * 0.95, headR * 0.09, 6, 18]} /><meshStandardMaterial color={spec.hair.color} roughness={0.8} /></mesh>}
            {/* the fall — behind and to both sides, to the waist */}
            <mesh position={[0, -h * 0.05, -headR * 0.85]} scale={[1.4, 1, 0.55]}><capsuleGeometry args={[headR * 0.6, h * 0.3, 4, 10]} />{hairMat}</mesh>
            {[-1, 1].map((s) => (
              <BeadChain key={s} pts={[[s * headR * 0.85, headR * 0.9, headR * 0.3], [s * headR * 1.05, headR * 0.1, headR * 0.45], [s * headR * 1.1, -h * 0.1, headR * 0.5], [s * headR * 0.9, -h * 0.24, headR * 0.35]]} r0={headR * 0.32} r1={headR * 0.18} mat={hairMat} />
            ))}
          </group>
        )}
        {hairStyle === "updo" && (
          <group>
            <mesh position={[0, headR * 1.05, -headR * 0.1]} scale={[1.06, 0.85, 1.06]}><sphereGeometry args={[headR * 1.02, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.5]} />{hairMat}</mesh>
            <mesh position={[0, headR * 1.95, -headR * 0.35]} scale={[1.2, 0.9, 1]}><sphereGeometry args={[headR * 0.55, 12, 10]} />{hairMat}</mesh>
            {[-1, 1].map((s) => <BeadChain key={s} pts={[[s * headR * 0.7, headR * 1.3, headR * 0.6], [s * headR * 0.95, headR * 0.7, headR * 0.7], [s * headR * 0.9, headR * 0.2, headR * 0.6]]} r0={headR * 0.16} r1={headR * 0.08} mat={hairMat} />)}
          </group>
        )}
        {hairStyle === "mossy" && (
          <group>
            <mesh position={[0, headR * 1.1, -headR * 0.05]} scale={[1.04, 0.8, 1.04]}><sphereGeometry args={[headR * 1.02, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.5]} /><meshStandardMaterial color={hairC} roughness={1} /></mesh>
            {[[0, 2.0, 0.1, 0], [0.5, 1.85, -0.2, 0.5], [-0.55, 1.8, 0.1, -0.5], [0.2, 1.9, -0.6, 0.2], [-0.2, 1.95, 0.5, -0.1]].map((c, i) => (
              <mesh key={i} position={[c[0] * headR, c[1] * headR, c[2] * headR]} rotation={[c[2] * 0.6, 0, -c[3]]}><coneGeometry args={[headR * 0.14, headR * 0.5, 5]} /><meshStandardMaterial color={hairC} roughness={1} /></mesh>
            ))}
          </group>
        )}
        {hairStyle === "mane" && (
          <group>
            <mesh position={[0, headR * 1.1, -headR * 0.15]} scale={[1.06, 0.85, 1.06]}><sphereGeometry args={[headR * 1.02, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.55]} />{hairMat}</mesh>
            {/* THE RING — §4b: a mane is volume around and behind the head, never strands off it */}
            {Array.from({ length: 11 }, (_, i) => {
              const a = (i / 11) * Math.PI * 1.55 + Math.PI * 0.22, r = headR * 1.25;
              return <mesh key={i} position={[Math.cos(a) * r, headR * 0.55 - Math.abs(Math.sin(a)) * headR * 0.2, Math.sin(a) * -r * 0.7 - headR * 0.25]} scale={[1, 1.3, 1]}><sphereGeometry args={[headR * 0.5, 9, 7]} />{hairMat}</mesh>;
            })}
          </group>
        )}
        {spec.extras}
      </group>

      {/* ── TAIL ── a thin tufted line from the rear hip */}
      {spec.tail && (
        <group name="rig_tail" ref={reg("tail")} position={[0, hipY - h * 0.02, -h * 0.1]}>
          <BeadChain pts={[[0, 0, 0], [0, -h * 0.12, -h * 0.12], [0, -h * 0.28, -h * 0.16], [h * 0.05, -h * 0.38, -h * 0.1]]} r0={h * 0.018} r1={h * 0.012} mat={<meshStandardMaterial color={spec.tail.color} roughness={0.9} />} />
          <mesh position={[h * 0.06, -h * 0.4, -h * 0.09]} scale={[1, 1.5, 1]}><sphereGeometry args={[h * 0.03, 8, 6]} /><meshStandardMaterial color={spec.tail.tuft || spec.tail.color} roughness={0.95} /></mesh>
        </group>
      )}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// V'YEN'S COIL — she never stands; she rises off a coil she made. The
// coil sweeps from under her waist DOWN and to the LEFT (-x, +z), the
// way the painting has it and the way the zone table thins it. Bead
// chain on a spiral, scaled purple, gems every fourth bead.
// ═══════════════════════════════════════════════════════════════════
function SerpentCoil({ h, hipY, spec }) {
  const scales = useMemo(() => { const t = skinTex("vyen-coil", "#3a2a5a", "scales"); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 3); return t; }, []);
  const beads = useMemo(() => {
    const pts = [];
    const turns = spec.turns ?? 1.9, reach = spec.reach ?? 3.6, n = 34;
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      const a = Math.PI * 0.9 + u * Math.PI * 2 * turns;
      const r = 0.25 + u * reach;
      // -x and +z bias: the coil falls down-left
      const x = Math.cos(a) * r * 0.9 - u * reach * 0.35;
      const z = Math.sin(a) * r * 0.55 + u * reach * 0.28;
      const y = Math.max(0.16, hipY * 0.85 - u * hipY * 0.7 + Math.sin(u * Math.PI * 3) * 0.12);
      pts.push({ p: [x, y, z], r: h * (0.13 - u * 0.09) });
    }
    return pts;
  }, [h, hipY, spec.turns, spec.reach]);
  return (
    <group>
      {beads.map((b, i) => (
        <group key={i} position={b.p}>
          <mesh castShadow={i % 3 === 0}><sphereGeometry args={[b.r, 10, 8]} /><meshStandardMaterial map={scales} roughness={0.55} metalness={0.15} /></mesh>
          {i % 4 === 2 && i < beads.length - 3 && (
            <mesh position={[0, b.r * 0.85, 0]}><octahedronGeometry args={[b.r * 0.35, 0]} /><meshStandardMaterial color={i % 8 === 2 ? "#e8453c" : "#B388FF"} emissive={i % 8 === 2 ? "#e8453c" : "#B388FF"} emissiveIntensity={0.9} roughness={0.2} toneMapped={false} /></mesh>
          )}
        </group>
      ))}
      {/* the tail tip — a slim tapered fin */}
      <mesh position={beads[beads.length - 1].p} rotation={[0, 0.6, 0]} scale={[1, 0.5, 2.2]}><sphereGeometry args={[h * 0.045, 8, 6]} /><meshStandardMaterial map={scales} roughness={0.55} /></mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE SPECS — one per person. Built once at module load (the textures
// are cached canvases; drawing them here is not "PRNG during render").
// ═══════════════════════════════════════════════════════════════════
const RAM_HORN = ({ s, headR }) => {
  // curled ram horn: a bead chain along a spiral, cream, ridged by taper
  const pts = [];
  for (let i = 0; i <= 8; i++) { const a = i / 8 * Math.PI * 1.55; const r = headR * (0.55 + i * 0.05); pts.push([s * (headR * 0.55 + Math.sin(a) * r * 0.6), headR * 1.35 + Math.cos(a) * r * 0.75 - i * headR * 0.06, -headR * 0.15 + i * headR * 0.06]); }
  return <BeadChain pts={pts} r0={headR * 0.2} r1={headR * 0.07} mat={<meshStandardMaterial color="#e6d3a3" roughness={0.6} />} />;
};

const BookProp = () => (
  <group rotation={[0.3, 0, 0]} position={[0, -0.02, 0.06]}>
    {[-1, 1].map((s) => <mesh key={s} position={[s * 0.06, 0, 0]} rotation={[0, s * 0.35, 0]}><boxGeometry args={[0.12, 0.14, 0.012]} /><meshStandardMaterial color="#d8caa0" /></mesh>)}
    <mesh position={[0, 0, -0.01]}><boxGeometry args={[0.25, 0.15, 0.01]} /><meshStandardMaterial color="#5a3a22" roughness={0.9} /></mesh>
    <mesh position={[0, 0, -0.016]}><planeGeometry args={[0.08, 0.08]} /><meshBasicMaterial color="#7ce7c0" toneMapped={false} /></mesh>
  </group>
);
const PackProp = ({ color }) => (
  <mesh rotation={[0.2, 0, 0]}><boxGeometry args={[0.12, 0.16, 0.025]} /><meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.35} roughness={0.4} /></mesh>
);

export function buildSpecs() {
  const S = {};
  // ── V'AEL, the Lorde's avatar ──
  S.vael = {
    key: "vael", h: 1.5, headR: 0.11, skin: "#f1dfd0",
    face: { eye: "#3fa860", eyeHot: "#d9ffe6", eyeRim: "#0e3a1e", brow: "#b9ab9a", lash: "#2a1a1a", mouth: "smirk", teeth: true, crescent: "#f4ead3", browTilt: -2, lip: "#8a3a4a" },
    hair: { color: "#e6e2dc", style: "updo" }, ears: "none",
    top: { tex: clothTex("vael-robe", "#141216", { seed: 3 }) }, sleeves: "#151317", hands: "#f1dfd0",
    coat: { tex: clothTex("vael-robe", "#141216", { seed: 3 }), hem: 0.06 }, legs: "#f1dfd0", boots: "#151517",
    collar: "#f3f0ea", necklace: true, tail: { color: "#1a1418", tuft: "#111" },
    aura: "#B388FF", idle: "lorde", phase: 1.3,
    extras: null,
  };
  S.vael.extras = [<RAM_HORN key="l" s={-1} headR={S.vael.h * S.vael.headR} />, <RAM_HORN key="r" s={1} headR={S.vael.h * S.vael.headR} />];

  // ── WILLY, the groundskeeper ──
  S.willy = {
    key: "willy", h: 0.95, headR: 0.17, skin: "#7c8a4a", skinMap: skinTex("willy", "#7c8a4a", "moss"),
    face: { eye: "#6b3a1e", eyeHot: "#f0c090", eyeRim: "#2a1206", eyeW: 15, eyeH: 12, brow: "#4a5a2a", lash: "#2a3010", mouth: "frown", lip: "#4a4a30", browTilt: 3 },
    hair: { color: "#5d7a33", style: "mossy" }, ears: "none",
    top: { color: "#5a5f5a" }, vest: "#6b4a2a", sleeves: "#7c8a4a", hands: "#7c8a4a",
    legs: "#5a4530", boots: "#7c8a4a",
    held: { L: <BookProp /> }, idle: "read", phase: 2.2,
  };

  // ── V'YEN, Sweet Treats ──
  S.vyen = {
    key: "vyen", h: 1.25, headR: 0.11, skin: "#b9a6d6",
    face: { eye: "#d8c8ff", eyeHot: "#ffffff", eyeRim: "#3a2a6a", brow: "#e8e2f0", lash: "#3a2a4a", mouth: "smile", lip: "#7a3a5a", slit: true, blush: "rgba(200,120,200,0.35)" },
    hair: { color: "#f1eef8", style: "long" }, ears: "default",
    top: { tex: clothTex("vyen-top", "#3a3a8a", { seed: 11, trim: "#e0b45a" }) }, sleeves: "#3a3a8a", hands: "#b9a6d6",
    coil: { turns: 1.9, reach: 3.8 }, necklace: true, idle: "ledger", phase: 0.7,
    extras: (
      <group>
        <mesh position={[0, 0.34, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.135, 0.012, 6, 18]} /><meshStandardMaterial color="#e0b45a" metalness={0.9} roughness={0.3} /></mesh>
        <mesh position={[0, 0.34, 0.135]}><octahedronGeometry args={[0.03, 0]} /><meshStandardMaterial color="#B388FF" emissive="#B388FF" emissiveIntensity={0.9} toneMapped={false} /></mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * 0.14, 0.02, 0]}><octahedronGeometry args={[0.022, 0]} /><meshStandardMaterial color="#e8453c" emissive="#e8453c" emissiveIntensity={0.8} toneMapped={false} /></mesh>)}
      </group>
    ),
  };

  // ── VENUS, the card packs ──
  S.venus = {
    key: "venus", h: 1.35, headR: 0.115, skin: "#c9a06a",
    face: { eye: "#c9d84a", eyeHot: "#fff8d0", eyeRim: "#3a3a10", brow: "#6a4a2a", lash: "#3a2a1a", mouth: "smile", teeth: true, lip: "#8a4a4a", slit: true, glasses: "#e9d9b5" },
    hair: { color: "#8a5a2a", style: "mane" }, ears: "round",
    top: { tex: clothTex("venus-dress", "#e9dcc0", { seed: 5, trim: "#c9a24c" }) }, sleeves: "#e9dcc0", hands: "#c9a06a",
    coat: { tex: clothTex("venus-dress", "#e9dcc0", { seed: 5, trim: "#c9a24c" }), hem: 0.1 }, legs: "#c9a06a", boots: "#5a3a22",
    tabard: "#c9a24c", tail: { color: "#c9a06a", tuft: "#6a3a1a" },
    held: { L: <PackProp color="#7c3a9c" />, R: <PackProp color="#c9a24c" /> }, idle: "present", phase: 3.1,
  };

  // ── THE AENARCHIST ──
  S.player_m = {
    key: "player_m", h: PERSON_H, headR: 0.115, skin: "#c58f66",
    face: { eye: "#3a8a4a", eyeHot: "#d8ffd8", eyeRim: "#0e2a12", brow: "#efeae2", lash: "#2a1a10", mouth: "flat", beard: "#2a2220", lip: "#6a3a3a" },
    hair: { color: "#efeae2", style: "swept" }, ears: "default",
    top: { tex: clothTex("player-m-coat", "#161418", { seed: 21, trim: "#c9a24c" }) }, sleeves: "#161418", hands: "#c58f66",
    coat: { tex: clothTex("player-m-coat", "#161418", { seed: 21, trim: "#c9a24c" }), hem: 0.14 }, legs: "#1e1b22", boots: "#2a2420",
    shirt: "#f4f1ea", sash: "#2a2530", idle: "stand", arms: "pockets", phase: 0,
  };
  S.player_f = {
    key: "player_f", h: PERSON_H, headR: 0.115, skin: "#f0dccc",
    face: { eye: "#8a5ad0", eyeHot: "#f0e8ff", eyeRim: "#2a1a4a", brow: "#e8e2da", lash: "#3a2a3a", mouth: "smile", lip: "#9a4a5a", blush: "rgba(220,120,160,0.3)" },
    hair: { color: "#efe9e2", style: "long", braid: true }, ears: "default",
    top: { tex: clothTex("player-f-robe", "#4a2a5a", { seed: 33 }) }, sleeves: "#4a2a5a", hands: "#f0dccc",
    coat: { tex: clothTex("player-f-robe", "#4a2a5a", { seed: 33 }), hem: 0.1 }, legs: "#2a1a30", boots: "#3a2a22",
    tabard: "#c9a24c", sash: "#1a1418", idle: "stand", phase: 0.5,
  };
  return S;
}

let _specs = null;
export function personSpec(id) {
  if (!_specs) _specs = buildSpecs();
  return _specs[id] || null;
}
export const playerSpecFor = (gender) => personSpec(gender === "f" ? "player_f" : "player_m");

// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
//  THE BESPOKE BODIES — V'AEL and LYRAEI   (caP'n, 2026-09-16, session 2)
// ═══════════════════════════════════════════════════════════════════
// "try your absolute hardest to make her look exactly like the V'ael in
// this picture … bar for bar like the pics." A parametric person gets a
// body 80% right; the last 20% is anatomy the picture actually has —
// the V cut to the navel, the capelet collar, the slit skirt with the
// legs showing, the ram spiral that curls forward and DOWN beside the
// cheek. So these two are sculpted by hand, part by part, against the
// art, and the parametric body stays for everyone else.
//
// Proportions are read off the pictures as fractions of crown height
// (u = fraction × h): V'ael's chin 0.80, shoulders 0.72, bust 0.64,
// waist 0.53, hips 0.46, knees 0.25. Lyraei's are hidden by the robe,
// so hers are the robe's: shoulders 0.74, sash 0.55, hem 0.02.
//
// Same contract as everything else in this file: origin, standing on
// y=0, facing +z at yaw 0, one useFrame, no lights, rig-slot names.
// ═══════════════════════════════════════════════════════════════════

// A finer face: 256², almond eyes with a lash wing, a real iris, a
// mouth that can smirk with teeth, the crescent, beauty marks, blush.
export function faceTex2(key, o) {
  return canvasTex("plaza3d:face2:" + key, 256, 256, (g, w, h) => {
    g.clearRect(0, 0, w, h);
    const cx = 128, eyeY = o.eyeY || 118, ex = o.eyeGap || 44, ew = o.eyeW || 27, eh = o.eyeH || 17;
    // brow shadow + cheek warmth
    const bs = g.createLinearGradient(0, 60, 0, 140); bs.addColorStop(0, "rgba(0,0,0,0)"); bs.addColorStop(0.45, "rgba(40,10,30,0.10)"); bs.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = bs; g.fillRect(0, 60, w, 80);
    if (o.blush) for (const s of [-1, 1]) { const bl = g.createRadialGradient(cx + s * 62, 150, 0, cx + s * 62, 150, 26); bl.addColorStop(0, o.blush); bl.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = bl; g.fillRect(cx + s * 62 - 26, 124, 52, 52); }
    for (const s of [-1, 1]) {
      const x = cx + s * ex;
      // white — almond, tilted up at the outer corner (the wing)
      g.save(); g.translate(x, eyeY); g.rotate(-s * (o.tilt ?? 0.12));
      g.fillStyle = "#f7f3ee"; g.beginPath(); g.moveTo(-ew, 0); g.quadraticCurveTo(-ew * 0.4, -eh * 1.25, ew * 0.7, -eh * 0.9); g.quadraticCurveTo(ew * 1.05, -eh * 0.2, ew * 0.85, eh * 0.35); g.quadraticCurveTo(0, eh * 1.15, -ew, 0); g.closePath(); g.fill();
      g.clip();
      // iris + pupil + light
      const ix = s * (o.look ?? 3), iy = 2;
      const ir = g.createRadialGradient(ix, iy, 2, ix, iy, eh * 0.95); ir.addColorStop(0, o.eyeHot || "#fff"); ir.addColorStop(0.3, o.eye); ir.addColorStop(0.85, o.eyeRim || "#0e2a14"); ir.addColorStop(1, "#000");
      g.fillStyle = ir; g.beginPath(); g.arc(ix, iy, eh * 0.95, 0, Math.PI * 2); g.fill();
      g.fillStyle = "#050505"; g.beginPath(); g.ellipse(ix, iy, eh * 0.36, eh * (o.slit ? 0.7 : 0.42), 0, 0, Math.PI * 2); g.fill();
      g.fillStyle = "rgba(255,255,255,0.9)"; g.beginPath(); g.arc(ix - 5, iy - 6, 3.4, 0, Math.PI * 2); g.fill();
      g.fillStyle = "rgba(255,255,255,0.5)"; g.beginPath(); g.arc(ix + 5, iy + 5, 1.8, 0, Math.PI * 2); g.fill();
      // upper lid shadow inside the white
      const ls = g.createLinearGradient(0, -eh, 0, -eh * 0.2); ls.addColorStop(0, "rgba(60,30,50,0.45)"); ls.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = ls; g.fillRect(-ew, -eh * 1.3, ew * 2.2, eh * 1.1);
      g.restore();
      // the lash line + wing
      g.save(); g.translate(x, eyeY); g.rotate(-s * (o.tilt ?? 0.12));
      g.strokeStyle = o.lash || "#241418"; g.lineCap = "round"; g.lineWidth = 4.2; g.beginPath(); g.moveTo(-ew * 0.95, 1); g.quadraticCurveTo(-ew * 0.35, -eh * 1.3, ew * 0.75, -eh * 0.95); g.stroke();
      g.lineWidth = 3; g.beginPath(); g.moveTo(ew * 0.75, -eh * 0.95); g.lineTo(ew * 1.25 * (s > 0 ? 1 : 1), -eh * 1.15); g.stroke();
      g.lineWidth = 1.6; g.strokeStyle = "rgba(36,20,24,0.55)"; g.beginPath(); g.moveTo(-ew * 0.7, eh * 0.55); g.quadraticCurveTo(0, eh * 1.15, ew * 0.8, eh * 0.4); g.stroke();
      g.restore();
      // brow
      g.strokeStyle = o.brow || "#8a7a68"; g.lineWidth = o.browW || 4; g.lineCap = "round"; g.beginPath();
      const arch = o.browArch ?? 14, lift = o.browLift ?? 0;
      // inner end (toward the nose) is x - s*ew; `browIn` lowers it (a
      // confident, knitted brow), `browOut` lifts the outer end
      g.moveTo(x - s * ew * 1.0, eyeY - eh - 10 + (o.browIn ?? 0) + lift * 0);
      g.quadraticCurveTo(x + s * ew * 0.1, eyeY - eh - 14 - arch, x + s * ew * 1.2, eyeY - eh - 10 - (o.browOut ?? 0));
      g.stroke();
    }
    // nose — a soft shadow and a tip line
    g.strokeStyle = "rgba(60,30,40,0.35)"; g.lineWidth = 2.5; g.lineCap = "round"; g.beginPath(); g.moveTo(cx - 2, 150); g.quadraticCurveTo(cx - 8, 162, cx + 3, 165); g.stroke();
    // mouth
    const my = o.mouthY || 190;
    g.lineWidth = 3.2; g.strokeStyle = o.lip || "#8a3a4a"; g.lineCap = "round";
    if (o.mouth === "smirk") {
      // wide, asymmetric — one corner up, teeth showing
      g.fillStyle = "#3a1520"; g.beginPath(); g.moveTo(cx - 30, my + 2); g.quadraticCurveTo(cx - 6, my + 18, cx + 34, my - 6); g.quadraticCurveTo(cx + 8, my - 2, cx - 30, my + 2); g.closePath(); g.fill();
      g.fillStyle = "#fbfbfb"; g.beginPath(); g.moveTo(cx - 24, my + 2); g.quadraticCurveTo(cx - 2, my + 10, cx + 30, my - 5); g.quadraticCurveTo(cx + 6, my + 1, cx - 24, my + 2); g.closePath(); g.fill();
      g.beginPath(); g.moveTo(cx - 30, my + 2); g.quadraticCurveTo(cx - 6, my + 18, cx + 34, my - 6); g.stroke();
      g.lineWidth = 2; g.beginPath(); g.moveTo(cx - 30, my + 2); g.quadraticCurveTo(cx + 6, my - 3, cx + 34, my - 6); g.stroke();
    } else if (o.mouth === "soft") {
      g.lineWidth = 2.6; g.beginPath(); g.moveTo(cx - 16, my); g.quadraticCurveTo(cx, my + 8, cx + 16, my); g.stroke();
      g.fillStyle = o.lip; g.globalAlpha = 0.55; g.beginPath(); g.moveTo(cx - 16, my); g.quadraticCurveTo(cx, my - 6, cx + 16, my); g.quadraticCurveTo(cx, my + 8, cx - 16, my); g.fill(); g.globalAlpha = 1;
    } else { g.beginPath(); g.moveTo(cx - 14, my); g.lineTo(cx + 14, my); g.stroke(); }
    // the crescent on the brow
    if (o.crescent) {
      g.save(); g.fillStyle = o.crescent; g.shadowColor = o.crescent; g.shadowBlur = 8;
      g.beginPath(); g.arc(cx, 60, 15, 0, Math.PI * 2); g.fill();
      g.globalCompositeOperation = "destination-out"; g.shadowBlur = 0; g.beginPath(); g.arc(cx + 7, 55, 13, 0, Math.PI * 2); g.fill();
      g.restore();
    }
    // beauty marks — [x, y] pairs
    if (o.marks) { g.fillStyle = o.markColor || "#7a6a5a"; for (const [mx, my2] of o.marks) { g.beginPath(); g.arc(cx + mx, my2, 2.6, 0, Math.PI * 2); g.fill(); } }
  });
}

// A striped horn — the ridges of a ram horn are painted, not modelled.
const hornTex = () => canvasTex("plaza3d:horn", 128, 128, (g, w, h) => {
  g.fillStyle = "#d9c49c"; g.fillRect(0, 0, w, h);
  for (let y = 0; y < h; y += 16) { g.fillStyle = "rgba(120,95,55,0.22)"; g.fillRect(0, y, w, 4); g.fillStyle = "rgba(255,244,220,0.22)"; g.fillRect(0, y + 6, w, 2); }
  const R = mulberry32(0x40a); for (let i = 0; i < 120; i++) { g.fillStyle = `rgba(90,70,40,${R() * 0.18})`; g.fillRect(R() * w, R() * h, 2, 5); }
});
// Silver hair with a soft sheen
const hairTex = (key, base, hi) => canvasTex("plaza3d:hair:" + key, 128, 128, (g, w, h) => {
  g.fillStyle = base; g.fillRect(0, 0, w, h);
  const R = mulberry32(0x4a1);
  for (let i = 0; i < 110; i++) { g.strokeStyle = R() < 0.5 ? hi : "rgba(90,80,90,0.16)"; g.lineWidth = 1 + R() * 1.2; g.beginPath(); const x = R() * w; g.moveTo(x, 0); g.bezierCurveTo(x + 10, h * 0.3, x - 10, h * 0.6, x + 4, h); g.stroke(); }
});
// V'ael's torso: black cloth with the V cut to the navel, PAINTED so it
// follows the curve of the body. u=0.25 is the front of a sphere.
const vaelTorsoTex = (skin) => canvasTex("plaza3d:vael-torso", 256, 256, (g, w, h) => {
  const R = mulberry32(0x3e1);
  g.fillStyle = "#17151a"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 700; i++) { g.fillStyle = `rgba(255,255,255,${R() * 0.05})`; g.fillRect(R() * w, R() * h, 1, 3); }
  const cx = 64;   // the front
  // the wedge: collar-wide at the top, closing at the navel
  g.fillStyle = skin; g.beginPath(); g.moveTo(cx - 30, 6); g.quadraticCurveTo(cx - 9, 120, cx, 214); g.quadraticCurveTo(cx + 9, 120, cx + 30, 6); g.closePath(); g.fill();
  // the bust reads through the wedge as two soft shadows either side of the cleavage
  for (const s of [-1, 1]) { const b = g.createRadialGradient(cx + s * 16, 70, 2, cx + s * 16, 70, 26); b.addColorStop(0, 'rgba(0,0,0,0)'); b.addColorStop(0.7, 'rgba(120,70,70,0.12)'); b.addColorStop(1, 'rgba(90,50,50,0.30)'); g.fillStyle = b; g.fillRect(cx + s * 16 - 26, 44, 52, 52); }
  // the cleavage line and the navel
  g.strokeStyle = "rgba(120,70,70,0.35)"; g.lineWidth = 2; g.beginPath(); g.moveTo(cx, 60); g.lineTo(cx, 120); g.stroke();
  g.fillStyle = "rgba(120,70,70,0.45)"; g.beginPath(); g.ellipse(cx, 196, 2.2, 3.2, 0, 0, Math.PI * 2); g.fill();
  // the seam of the dress along the wedge
  g.strokeStyle = "rgba(0,0,0,0.6)"; g.lineWidth = 2; g.beginPath(); g.moveTo(cx - 30, 6); g.quadraticCurveTo(cx - 9, 120, cx, 214); g.quadraticCurveTo(cx + 9, 120, cx + 30, 6); g.stroke();
});
// Lyraei's tabard — gold with the sigil: a ringed disc, a spiral, a diamond.
const tabardTex = () => canvasTex("plaza3d:lyraei-tabard", 128, 512, (g, w, h) => {
  const gr = g.createLinearGradient(0, 0, w, 0); gr.addColorStop(0, "#8f6a2a"); gr.addColorStop(0.5, "#c9a24c"); gr.addColorStop(1, "#8f6a2a");
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
  g.strokeStyle = "#5a3f14"; g.lineWidth = 4; g.strokeRect(8, 8, w - 16, h - 16); g.lineWidth = 2; g.strokeRect(16, 16, w - 32, h - 32);
  g.lineWidth = 5; g.lineCap = "round";
  g.beginPath(); g.arc(64, 70, 26, 0, Math.PI * 2); g.stroke(); g.beginPath(); g.arc(64, 70, 9, 0, Math.PI * 2); g.stroke();
  g.beginPath(); g.moveTo(64, 96); g.lineTo(64, 130); g.stroke();
  // the spiral glyph
  g.beginPath(); for (let t = 0; t < Math.PI * 3.2; t += 0.1) { const r = 4 + t * 4.2; const x = 64 + Math.cos(t) * r, y = 210 + Math.sin(t) * r; if (t === 0) g.moveTo(x, y); else g.lineTo(x, y); } g.stroke();
  g.beginPath(); g.arc(64, 300, 7, 0, Math.PI * 2); g.fillStyle = "#5a3f14"; g.fill();
  g.beginPath(); g.moveTo(64, 340); g.lineTo(96, 380); g.lineTo(64, 420); g.lineTo(32, 380); g.closePath(); g.stroke();
  g.beginPath(); g.moveTo(64, 360); g.lineTo(80, 380); g.lineTo(64, 400); g.lineTo(48, 380); g.closePath(); g.stroke();
});
const robeTex = () => canvasTex("plaza3d:lyraei-robe", 256, 256, (g, w, h) => {
  const R = mulberry32(0x71);
  g.fillStyle = "#6a4274"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 40; i++) { g.strokeStyle = `rgba(20,8,30,${0.12 + R() * 0.2})`; g.lineWidth = 2 + R() * 6; g.beginPath(); const x = R() * w; g.moveTo(x, 0); g.quadraticCurveTo(x + (R() - 0.5) * 30, h / 2, x + (R() - 0.5) * 20, h); g.stroke(); }
  for (let i = 0; i < 500; i++) { g.fillStyle = `rgba(255,220,255,${R() * 0.04})`; g.fillRect(R() * w, R() * h, 1, 2); }
});
const noteTex = () => canvasTex("plaza3d:note", 64, 64, (g) => { g.clearRect(0, 0, 64, 64); g.fillStyle = "#e08cff"; g.shadowColor = "#e08cff"; g.shadowBlur = 8; g.font = "bold 44px Georgia, serif"; g.textAlign = "center"; g.textBaseline = "middle"; g.fillText("♪", 32, 34); });

// A ram horn. Generated in the head's side plane — back, over the top,
// down the front, tip curling down-and-back beside the cheek — then the
// whole spiral is turned OUTWARD about y (`splay`) so that from the front
// it reads as the face-on spiral the picture shows, and it flares out as
// it goes. Alternating bead radii are the ridges; the striped canvas is
// the grain. Thick at the base, a point at the tip.
export function RamHorn({ s, R0, base = [0, 0, 0], splay = 0.75, mat = null, thick = 1 }) {
  const tex = useMemo(() => hornTex(), []);
  const beads = useMemo(() => {
    const out = [];
    const n = 44;
    const cs = Math.cos(splay), sn = Math.sin(splay);
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const a = Math.PI * 1.0 - t * Math.PI * 1.85;         // back → top → front → down → back
      const r = R0 * (1.0 - t * 0.55);                      // the spiral tightens
      const ly = Math.sin(a) * r;                           // in-plane up
      const lz = Math.cos(a) * r + R0 * 0.15;               // in-plane forward
      const lx = R0 * 0.12 + t * R0 * 0.6;                  // out of the skull, flaring as it goes
      // turn the side plane outward about y so the spiral faces the viewer
      const x = s * (lx * cs + lz * sn);
      const z = lz * cs - lx * sn;
      out.push({ p: [base[0] + x, base[1] + ly, base[2] + z], r: R0 * (0.3 - t * 0.25) * thick * (i % 2 ? 1 : 0.965) });
    }
    return out;
  }, [s, R0, base[0], base[1], base[2], splay, thick]);
  const geo = useMemo(() => mergeBeads(beads), [beads]);
  return <mesh geometry={geo} castShadow>{mat || <meshStandardMaterial map={tex} roughness={0.55} />}</mesh>;
}

// A wavy lock of hair: a bead chain along a sine-waved line.
function Lock({ from, to, waves = 2, amp = 0.02, r0, r1, mat, side = 1, bScale = null }) {
  const pts = useMemo(() => {
    const out = [];
    for (let i = 0; i <= 8; i++) { const t = i / 8; out.push([from[0] + (to[0] - from[0]) * t + Math.sin(t * Math.PI * waves) * amp * side, from[1] + (to[1] - from[1]) * t, from[2] + (to[2] - from[2]) * t + Math.cos(t * Math.PI * waves) * amp * 0.5]); }
    return out;
  }, [from, to, waves, amp, side]);
  return <BeadChain pts={pts} r0={r0} r1={r1} mat={mat} bScale={bScale} />;
}

// ═══════════════════════════════════════════════════════════════════
// V'AEL — The Lorde's avatar, off `vael.webp`.
// ═══════════════════════════════════════════════════════════════════
export const VAEL_H = 1.5;
export function VaelBody({ ctl, h = VAEL_H }) {
  const root = useRef(); const head = useRef(); const tail = useRef(); const chest = useRef(); const armL = useRef(); const armR = useRef(); const hips = useRef();
  const yaw = useRef(0); const ph = useRef(0);
  const u = (v) => v * h;
  const SKIN = "#f9e6d0";
  const face = useMemo(() => faceTex2("vael", {
    eye: "#3fbf6a", eyeHot: "#e6ffef", eyeRim: "#0c3a1c", lash: "#241418", brow: "#8a7a68", browW: 5, browArch: 8, browIn: 10, browOut: 2, tilt: 0.14, look: 2,
    eyeW: 33, eyeH: 21, eyeY: 124, eyeGap: 46, mouthY: 196,
    mouth: "smirk", lip: "#8a3a4a", crescent: "#fff8e8", marks: [[0, 96], [-60, 156], [60, 156]], markColor: "#8a7666", blush: "rgba(230,120,140,0.2)",
  }), []);
  const torsoTex = useMemo(() => vaelTorsoTex(SKIN), []);
  const hair = useMemo(() => hairTex("vael", "#ebe7e2", "rgba(255,255,255,0.3)"), []);
  const hairMat = <meshStandardMaterial map={hair} roughness={0.7} />;
  // skin carries a whisper of warm emissive so a violet room never greys her
  const skinMat = <meshStandardMaterial color={SKIN} emissive="#5a3018" emissiveIntensity={0.3} roughness={0.55} />;
  const clothMat = <meshStandardMaterial color="#17151a" roughness={0.9} />;
  const headR = u(0.07);   // the picture's face is ~an eighth of her height

  useFrame(({ clock }, dtRaw) => {
    const o = root.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 1.3;
    const f = ctl.facing || { x: 0, y: 1 }; const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9); o.rotation.y = yaw.current;
    const speed = ctl.speed || 0; ph.current += speed * dt * 9;
    const br = 1 + Math.sin(t * 1.5) * 0.012;
    if (chest.current) chest.current.scale.set(1.06 / Math.sqrt(br), 1.05 * br, 0.78);
    if (hips.current) hips.current.rotation.z = Math.sin(t * 0.5) * 0.03;
    if (head.current) { head.current.rotation.y = Math.sin(t * 0.42) * 0.34; head.current.rotation.z = Math.sin(t * 0.27) * 0.05; head.current.rotation.x = -0.04 + Math.sin(t * 0.8) * 0.02; }
    if (tail.current) { tail.current.rotation.y = Math.sin(t * 1.7) * 0.5; tail.current.rotation.x = Math.sin(t * 2.3) * 0.15; }
    const sw = speed ? Math.sin(ph.current) * 0.5 : Math.sin(t * 0.9) * 0.03;
    if (armL.current) { armL.current.rotation.x = sw; armL.current.rotation.z = 0.16; }
    if (armR.current) { armR.current.rotation.x = -sw; armR.current.rotation.z = -0.16; }
    o.position.y = speed ? Math.abs(Math.sin(ph.current)) * u(0.02) : 0;
  });

  // the skirt: a NARROW front panel and a wide back panel, the sides open
  // from the hip down — that is the picture. Both hang from the hip and
  // flare a little to the ankle.
  const panel = (thetaStart, thetaLen, key) => (
    <mesh key={key} position={[0, u(0.25), 0]} castShadow>
      <cylinderGeometry args={[u(0.092), u(0.15), u(0.44), 16, 1, true, thetaStart, thetaLen]} />
      <meshStandardMaterial color="#17151a" roughness={0.9} side={THREE.DoubleSide} />
    </mesh>
  );
  return (
    <group ref={root}>
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.2), 16]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      <mesh position={[0, u(0.5), 0]}><sphereGeometry args={[u(0.42), 14, 10]} /><meshBasicMaterial color="#B388FF" transparent opacity={0.05} side={THREE.BackSide} depthWrite={false} toneMapped={false} /></mesh>

      {/* ── LEGS: long, bare, a little forward of the hip so the slits show them ── */}
      {[-1, 1].map((s) => (
        <group key={s} name={s < 0 ? "rig_legL" : "rig_legR"} position={[s * u(0.06), u(0.46), u(0.02)]}>
          <Cap r={u(0.054)} l={u(0.16)} position={[0, -u(0.11), 0]} castShadow>{skinMat}</Cap>
          <mesh position={[0, -u(0.215), 0]}><sphereGeometry args={[u(0.046), 10, 8]} />{skinMat}</mesh>
          <Cap r={u(0.041)} l={u(0.15)} position={[0, -u(0.31), 0]} castShadow>{skinMat}</Cap>
          <Cap r={u(0.05)} l={u(0.06)} position={[0, -u(0.41), 0]}><meshStandardMaterial color="#141416" roughness={0.5} /></Cap>
          <mesh position={[0, -u(0.44), u(0.035)]} scale={[1.05, 0.6, 1.7]}><sphereGeometry args={[u(0.052), 10, 8]} /><meshStandardMaterial color="#141416" roughness={0.5} /></mesh>
        </group>
      ))}
      {/* ── HIPS, the skirt, the tail ── */}
      <group ref={hips}>
        <mesh position={[0, u(0.475), 0]} scale={[0.98, 0.55, 0.8]} castShadow><sphereGeometry args={[u(0.1), 14, 10]} />{clothMat}</mesh>
        {panel(-Math.PI * 0.14, Math.PI * 0.28, "front")}
        {panel(Math.PI * 0.5, Math.PI * 1.0, "back")}
        <group ref={tail} name="rig_tail" position={[0, u(0.45), -u(0.085)]}>
          <BeadChain seg={6} pts={[[0, 0, 0], [u(0.03), -u(0.03), -u(0.05)], [u(0.08), -u(0.08), -u(0.08)], [u(0.15), -u(0.13), -u(0.08)], [u(0.21), -u(0.18), -u(0.05)], [u(0.25), -u(0.24), u(0.0)], [u(0.265), -u(0.29), u(0.03)]]} r0={u(0.011)} r1={u(0.006)} mat={<meshStandardMaterial color="#141216" roughness={0.9} />} />
          <mesh position={[u(0.27), -u(0.325), u(0.045)]} rotation={[0.4, 0, -0.3]} scale={[1, 2.0, 1]}><sphereGeometry args={[u(0.02), 8, 6]} /><meshStandardMaterial color="#0f0e12" roughness={1} /></mesh>
        </group>
      </group>
      {/* ── TORSO: an hourglass — the chest, the narrow waist, the V painted into the cloth ── */}
      <mesh position={[0, u(0.548), 0]} scale={[0.86, 0.95, 0.72]}><sphereGeometry args={[u(0.078), 14, 10]} />{clothMat}</mesh>
      <mesh ref={chest} position={[0, u(0.645), 0]} scale={[1.06, 1.05, 0.78]} castShadow>
        <sphereGeometry args={[u(0.1), 18, 14]} />
        <meshStandardMaterial map={torsoTex} roughness={0.85} />
      </mesh>
      {/* the white collar: a high band and the capelet over the shoulders, open at the front */}
      <mesh position={[0, u(0.765), 0]}><cylinderGeometry args={[u(0.04), u(0.045), u(0.05), 12, 1, true]} /><meshStandardMaterial color="#f4f2ee" roughness={0.6} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, u(0.72), 0]}>
        <cylinderGeometry args={[u(0.046), u(0.108), u(0.06), 16, 1, true, Math.PI * 0.14, Math.PI * 1.72]} />
        <meshStandardMaterial color="#f4f2ee" roughness={0.6} side={THREE.DoubleSide} />
      </mesh>
      {/* the cross on its cord, hanging into the V */}
      <group position={[0, u(0.735), u(0.08)]}>
        {[-1, 1].map((s) => <mesh key={s} position={[s * u(0.022), -u(0.05), u(0.01)]} rotation={[0.15, 0, s * 0.4]}><cylinderGeometry args={[u(0.0025), u(0.0025), u(0.11), 4]} /><meshStandardMaterial color="#1a1618" /></mesh>)}
        <group position={[0, -u(0.115), u(0.022)]}>
          <mesh><boxGeometry args={[u(0.008), u(0.046), u(0.005)]} /><meshStandardMaterial color="#d8dde6" metalness={0.9} roughness={0.25} /></mesh>
          <mesh position={[0, u(0.009), 0]}><boxGeometry args={[u(0.028), u(0.007), u(0.005)]} /><meshStandardMaterial color="#d8dde6" metalness={0.9} roughness={0.25} /></mesh>
        </group>
      </group>
      {/* ── ARMS: black to three-quarter, bare forearm, the hand — close to the body ── */}
      {[-1, 1].map((s) => (
        <group key={s} ref={s < 0 ? armL : armR} name={s < 0 ? "rig_armL" : "rig_armR"} position={[s * u(0.108), u(0.705), 0]}>
          <mesh><sphereGeometry args={[u(0.031), 10, 8]} />{clothMat}</mesh>
          {/* three-quarter sleeve: cloth to just past the elbow, then a bare forearm */}
          <Cap r={u(0.028)} l={u(0.13)} position={[0, -u(0.09), 0]} castShadow>{clothMat}</Cap>
          <Cap r={u(0.024)} l={u(0.03)} position={[0, -u(0.175), 0]}>{clothMat}</Cap>
          <Cap r={u(0.021)} l={u(0.1)} position={[0, -u(0.255), 0]}>{skinMat}</Cap>
          <mesh position={[0, -u(0.335), u(0.005)]} scale={[0.8, 1.25, 0.5]}><sphereGeometry args={[u(0.027), 10, 8]} />{skinMat}</mesh>
        </group>
      ))}
      {/* ── HEAD ── */}
      <group ref={head} name="rig_head" position={[0, u(0.79), 0]}>
        <mesh position={[0, u(0.012), 0]}><cylinderGeometry args={[u(0.028), u(0.034), u(0.06), 10]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.93, 1.1, 0.96]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.93, 1.1, 0.96]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.24, Math.PI * 0.6]} />
          <meshStandardMaterial map={face} transparent roughness={0.62} />
        </mesh>
        {/* HAIR: the updo — a full cap to the temples, the bun high and back,
            a swept fringe, and SHORT S-curls framing the cheeks (curls that
            SIT beside the face, never strands that hang — §4b) */}
        {/* phi 0.5π is +z (the face): the cap runs from 0.95π round the back to 2.05π, and only the top 0.3π over the brow */}
        <mesh position={[0, headR * 1.12, -headR * 0.06]} scale={[1.1, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, Math.PI * 0.95, Math.PI * 1.1, 0, Math.PI * 0.62]} />{hairMat}</mesh>
        <mesh position={[0, headR * 1.12, -headR * 0.06]} scale={[1.1, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.3]} />{hairMat}</mesh>
        <mesh position={[0, headR * 2.05, -headR * 0.5]} scale={[1.3, 1.0, 1.1]}><sphereGeometry args={[headR * 0.6, 14, 12]} />{hairMat}</mesh>
        <mesh position={[headR * 0.35, headR * 2.2, -headR * 0.15]} rotation={[0.3, 0, -0.5]} scale={[1.3, 0.7, 1]}><sphereGeometry args={[headR * 0.4, 10, 8]} />{hairMat}</mesh>
        <mesh position={[-headR * 0.4, headR * 2.15, -headR * 0.05]} rotation={[0.2, 0, 0.6]} scale={[1.3, 0.7, 1]}><sphereGeometry args={[headR * 0.42, 10, 8]} />{hairMat}</mesh>
        <mesh position={[headR * 0.25, headR * 1.92, headR * 0.62]} rotation={[0.6, 0.2, -0.6]} scale={[1.7, 0.4, 0.7]}><sphereGeometry args={[headR * 0.34, 10, 8]} />{hairMat}</mesh>
        <mesh position={[-headR * 0.55, headR * 1.85, headR * 0.55]} rotation={[0.5, -0.3, 0.5]} scale={[1.1, 0.36, 0.7]}><sphereGeometry args={[headR * 0.32, 10, 8]} />{hairMat}</mesh>
        {[-1, 1].map((s) => (
          <group key={s}>
            {/* two SHORT loose curls at the temple — thick, springy, to the jaw and no further (a longer one is a braid, §4b) */}
            <Lock from={[s * headR * 0.8, headR * 1.62, headR * 0.55]} to={[s * headR * 1.12, headR * 0.85, headR * 0.62]} waves={1.6} amp={headR * 0.16} side={s} r0={headR * 0.17} r1={headR * 0.08} mat={hairMat} />
            <Lock from={[s * headR * 1.0, headR * 1.45, headR * 0.05]} to={[s * headR * 1.15, headR * 0.7, headR * 0.3]} waves={1.3} amp={headR * 0.12} side={-s} r0={headR * 0.15} r1={headR * 0.07} mat={hairMat} />
          </group>
        ))}
        {/* THE HORNS — as big as the head, from the temples, curling forward and down */}
        {[-1, 1].map((s) => <RamHorn key={s} s={s} R0={headR * 1.15} base={[s * headR * 0.9, headR * 1.3, -headR * 0.05]} splay={0.5} />)}
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LYRAEI — the Aenarchist, off `player_f_down_a.webp`.
// ═══════════════════════════════════════════════════════════════════
export const LYRAEI_H = PERSON_H;
export function LyraeiBody({ ctl, h = LYRAEI_H }) {
  const root = useRef(); const head = useRef(); const chest = useRef(); const note = useRef(); const hairBack = useRef(); const armL = useRef(); const armR = useRef();
  const yaw = useRef(0); const ph = useRef(0);
  const u = (v) => v * h;
  const SKIN = "#f4e6da";
  const face = useMemo(() => faceTex2("lyraei", {
    eye: "#9a6ae0", eyeHot: "#f4ecff", eyeRim: "#3a1e6a", lash: "#3a2a3a", brow: "#b8aca0", browW: 3, browArch: 10, browIn: 2, tilt: 0.06, look: 0,
    mouth: "soft", lip: "#b06070", blush: "rgba(220,120,160,0.28)", eyeW: 30, eyeH: 21, eyeY: 124, eyeGap: 44, mouthY: 194,
  }), []);
  const hair = useMemo(() => hairTex("lyraei", "#efeae4", "rgba(255,255,255,0.35)"), []);
  const robe = useMemo(() => robeTex(), []);
  const tabard = useMemo(() => tabardTex(), []);
  const noteMap = useMemo(() => noteTex(), []);
  const hairMat = <meshStandardMaterial map={hair} roughness={0.7} />;
  const skinMat = <meshStandardMaterial color={SKIN} emissive="#4a2a1e" emissiveIntensity={0.22} roughness={0.6} />;
  const robeMat = <meshStandardMaterial map={robe} roughness={0.92} />;
  const darkMat = <meshStandardMaterial color="#1c1424" roughness={0.95} />;
  const goldMat = <meshStandardMaterial map={tabard} roughness={0.4} metalness={0.3} emissive="#6a4a10" emissiveIntensity={0.25} />;
  const GLOW = "#d05cff";
  const headR = u(0.075);

  useFrame(({ clock }, dtRaw) => {
    const o = root.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 0.5;
    const f = ctl.facing || { x: 0, y: 1 }; const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 11); o.rotation.y = yaw.current;
    const speed = ctl.speed || 0; ph.current += speed * dt * 9.5;
    const br = 1 + Math.sin(t * 1.6) * 0.012;
    if (chest.current) chest.current.scale.set(1.15 / Math.sqrt(br), 1.3 * br, 0.82);
    if (head.current) { head.current.rotation.y = Math.sin(t * 0.5) * 0.16; head.current.rotation.z = Math.sin(t * 0.33) * 0.04; }
    if (hairBack.current) hairBack.current.rotation.x = Math.sin(t * 1.1) * 0.03 + (speed ? Math.sin(ph.current * 0.5) * 0.05 : 0);
    if (note.current) { const k = (t * 0.45) % 1; note.current.position.y = headR * 2.0 + k * headR * 1.2; note.current.material.opacity = Math.sin(k * Math.PI) * 0.9; note.current.position.x = headR * 1.6 + Math.sin(k * Math.PI * 2) * headR * 0.15; }
    // hands clasped low at the tabard; walking swings them a little
    const sw = speed ? Math.sin(ph.current) * 0.12 : Math.sin(t * 0.8) * 0.015;
    if (armL.current) { armL.current.rotation.x = -0.22 + sw; armL.current.rotation.z = 0.06; }
    if (armR.current) { armR.current.rotation.x = -0.22 - sw; armR.current.rotation.z = -0.06; }
    o.position.y = speed ? Math.abs(Math.sin(ph.current)) * u(0.02) : 0;
  });

  // the sprite's violet outline — a BackSide shell a hair larger than the mass it wraps
  const Glow = ({ r, pos, scale = [1, 1, 1], geo = "sphere", args, k = 1.07 }) => (
    <mesh position={pos} scale={scale.map((v) => v * k)}>
      {geo === "sphere" ? <sphereGeometry args={[r, 14, 10]} /> : geo === "capsule" ? <capsuleGeometry args={args} /> : <coneGeometry args={args} />}
      <meshBasicMaterial color={GLOW} transparent opacity={0.55} side={THREE.BackSide} depthWrite={false} toneMapped={false} />
    </mesh>
  );
  // a fan of wavy strands: `n` locks between two angles, each a different wave
  const Fall = ({ s, n = 3, top, bottom, spread, r0, r1 }) => (
    <group>
      {Array.from({ length: n }, (_, i) => {
        const k = n === 1 ? 0.5 : i / (n - 1);
        return <Lock key={i} from={[top[0] + s * spread[0] * (k - 0.5), top[1], top[2] + spread[2] * (k - 0.5)]} to={[bottom[0] + s * spread[1] * (k - 0.5), bottom[1], bottom[2] + spread[3] * (k - 0.5)]} waves={2.4 + i * 0.6} amp={headR * (0.22 + i * 0.06)} side={i % 2 ? -s : s} r0={r0} r1={r1} mat={hairMat} bScale={[1.7, 1, 0.55]} />;
      })}
    </group>
  );

  return (
    <group ref={root}>
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.2), 16]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      {/* ── BOOTS, laced, just showing under the hem ── */}
      {[-1, 1].map((s) => (
        <group key={s} name={s < 0 ? "rig_legL" : "rig_legR"} position={[s * u(0.045), u(0.12), u(0.01)]}>
          <Cap r={u(0.036)} l={u(0.07)} position={[0, -u(0.06), 0]}><meshStandardMaterial color="#3a2a22" roughness={0.6} /></Cap>
          <mesh position={[0, -u(0.1), u(0.03)]} scale={[1, 0.55, 1.6]}><sphereGeometry args={[u(0.04), 10, 8]} /><meshStandardMaterial color="#2e2119" roughness={0.6} /></mesh>
          {[0, 1, 2].map((i) => <mesh key={i} position={[0, -u(0.04) - i * u(0.018), u(0.036)]} rotation={[0, 0, i % 2 ? 0.5 : -0.5]}><boxGeometry args={[u(0.028), u(0.004), u(0.003)]} /><meshStandardMaterial color="#8a7a66" /></mesh>)}
        </group>
      ))}
      {/* ── THE ROBE: a fitted purple A-line, black underskirt at the hem ── */}
      <mesh position={[0, u(0.3), 0]} castShadow>
        <cylinderGeometry args={[u(0.105), u(0.19), u(0.5), 18, 1, true]} />
        <meshStandardMaterial map={robe} roughness={0.92} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, u(0.24), 0]}>
        <cylinderGeometry args={[u(0.12), u(0.185), u(0.42), 18, 1, true]} />
        <meshStandardMaterial color="#1c1424" roughness={0.95} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, u(0.03), 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.185), 18]} />{darkMat}</mesh>
      <Glow geo="capsule" args={[u(0.16), u(0.2), 4, 12]} pos={[0, u(0.24), 0]} scale={[1, 1.0, 1]} k={1.12} />
      {/* the hips and the sash */}
      <mesh position={[0, u(0.53), 0]} scale={[1.1, 0.7, 0.85]}><sphereGeometry args={[u(0.1), 12, 10]} />{robeMat}</mesh>
      <mesh position={[0, u(0.555), 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[u(0.105), u(0.02), 6, 18]} /><meshStandardMaterial color="#141018" roughness={0.9} /></mesh>
      {/* ── TORSO and the black mantle/hood ── */}
      <mesh ref={chest} position={[0, u(0.65), 0]} scale={[1.15, 1.3, 0.82]} castShadow><sphereGeometry args={[u(0.095), 16, 12]} />{robeMat}</mesh>
      <Glow r={u(0.095)} pos={[0, u(0.65), 0]} scale={[1.15, 1.3, 0.82]} />
      <mesh position={[0, u(0.715), 0]}>
        <cylinderGeometry args={[u(0.05), u(0.15), u(0.09), 16, 1, true, Math.PI * 0.22, Math.PI * 1.56]} />
        <meshStandardMaterial color="#1c1424" roughness={0.95} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, u(0.76), -u(0.06)]} scale={[1.15, 0.65, 1]}><sphereGeometry args={[u(0.07), 12, 10]} />{darkMat}</mesh>
      {/* THE TABARD — gold, the painted sigil, a pointed hem */}
      <mesh position={[0, u(0.41), u(0.108)]} rotation={[0.08, 0, 0]}>
        <planeGeometry args={[u(0.11), u(0.6)]} />
        {goldMat}
      </mesh>
      <mesh position={[0, u(0.1), u(0.132)]} rotation={[0.08, 0, Math.PI / 4]}>
        <planeGeometry args={[u(0.0778), u(0.0778)]} />
        <meshStandardMaterial color="#b48a3c" roughness={0.4} metalness={0.3} emissive="#6a4a10" emissiveIntensity={0.25} />
      </mesh>
      {/* ── ARMS: bell sleeves, the hands meeting at the tabard ── */}
      {[-1, 1].map((s) => (
        <group key={s} ref={s < 0 ? armL : armR} name={s < 0 ? "rig_armL" : "rig_armR"} position={[s * u(0.13), u(0.71), 0]}>
          <mesh><sphereGeometry args={[u(0.037), 10, 8]} />{robeMat}</mesh>
          <Cap r={u(0.034)} l={u(0.1)} position={[0, -u(0.09), 0]} castShadow>{robeMat}</Cap>
          {/* the forearm turns in and down so the hands meet at the sigil */}
          <group position={[0, -u(0.16), 0]} rotation={[-0.8, 0, -s * 1.0]}>
            <mesh position={[0, -u(0.075), 0]}><coneGeometry args={[u(0.062), u(0.15), 12, 1, true]} /><meshStandardMaterial map={robe} roughness={0.92} side={THREE.DoubleSide} /></mesh>
            <mesh position={[0, -u(0.15), 0]} rotation={[Math.PI / 2, 0, 0]}><ringGeometry args={[u(0.028), u(0.063), 12]} />{darkMat}</mesh>
            <mesh position={[0, -u(0.165), 0]} scale={[0.75, 1.2, 0.5]}><sphereGeometry args={[u(0.03), 10, 8]} />{skinMat}</mesh>
          </group>
        </group>
      ))}
      {/* ── HEAD ── */}
      <group ref={head} name="rig_head" position={[0, u(0.79), 0]}>
        <mesh position={[0, u(0.012), 0]}><cylinderGeometry args={[u(0.028), u(0.034), u(0.06), 10]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.94, 1.08, 0.96]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.94, 1.08, 0.96]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.24, Math.PI * 0.6]} />
          <meshStandardMaterial map={face} transparent roughness={0.62} />
        </mesh>
        <Glow r={headR} pos={[0, headR * 1.05, 0]} scale={[0.94, 1.08, 0.96]} k={1.08} />
        {/* HAIR: a back-and-top cap, a centre part with two swept sides, the braid crown */}
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.08, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, Math.PI * 0.92, Math.PI * 1.16, 0, Math.PI * 0.62]} />{hairMat}</mesh>
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.08, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.26]} />{hairMat}</mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.5, headR * 1.72, headR * 0.6]} rotation={[0.55, s * 0.5, -s * 0.9]} scale={[1.4, 0.36, 0.7]}><sphereGeometry args={[headR * 0.42, 10, 8]} />{hairMat}</mesh>)}
        <mesh position={[0, headR * 1.62, 0]} rotation={[Math.PI / 2 + 0.22, 0, 0]}><torusGeometry args={[headR * 1.0, headR * 0.09, 8, 26]} />{hairMat}</mesh>
        {Array.from({ length: 16 }, (_, i) => { const a = (i / 16) * Math.PI * 2; return <mesh key={i} position={[Math.cos(a) * headR * 1.0, headR * 1.62 - Math.sin(a) * headR * 0.23, Math.sin(a) * headR * 0.98]} rotation={[0, -a, 0.6]} scale={[1, 1.4, 1]}><sphereGeometry args={[headR * 0.1, 6, 5]} />{hairMat}</mesh>; })}
        {/* the back mass to the waist, and the FALLS: two fans of waves each side, over the shoulders, to the waist */}
        <group ref={hairBack} position={[0, headR * 1.2, -headR * 0.5]}>
          <mesh position={[0, -u(0.2), -headR * 0.2]} scale={[1.7, 1, 0.6]}><capsuleGeometry args={[headR * 0.6, u(0.34), 4, 12]} />{hairMat}</mesh>
          <Glow geo="capsule" args={[headR * 0.6, u(0.34), 4, 12]} pos={[0, -u(0.2), -headR * 0.2]} scale={[1.7, 1, 0.6]} k={1.1} />
        </group>
        {[-1, 1].map((s) => (
          <group key={s}>
            <Fall s={s} n={3} top={[s * headR * 0.85, headR * 1.35, headR * 0.3]} bottom={[s * headR * 1.5, -u(0.34), headR * 0.85]} spread={[headR * 0.3, headR * 0.9, headR * 0.3, headR * 0.5]} r0={headR * 0.15} r1={headR * 0.07} />
            <Fall s={s} n={2} top={[s * headR * 1.0, headR * 1.15, -headR * 0.25]} bottom={[s * headR * 1.7, -u(0.3), headR * 0.1]} spread={[headR * 0.2, headR * 0.6, headR * 0.2, headR * 0.5]} r0={headR * 0.14} r1={headR * 0.06} />
          </group>
        ))}
        <mesh ref={note} position={[headR * 1.6, headR * 2.0, headR * 0.3]}>
          <planeGeometry args={[headR * 0.6, headR * 0.6]} />
          <meshBasicMaterial map={noteMap} transparent opacity={0} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  THE REST OF THE FOLK — bespoke, off the painting (session 3)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "build the rest of the plaza with the same love and attention."
// Willy off `willy.webp` and his corner of `plaza_1.webp`; V'yen and
// Venus off their corners of the painting; the male Aenarchist off
// `player_m_down_a.webp`. Same law as V'ael: sculpted part by part
// against the art in the review room, never from memory.

// A tattooed lavender skin (V'yen) — dark indigo swirls on grey-violet.
const tattooSkinTex = () => canvasTex("plaza3d:skin:vyen-ink", 256, 256, (g, w, h) => {
  const R = mulberry32(0x1a77);
  g.fillStyle = "#9d93b4"; g.fillRect(0, 0, w, h);
  g.strokeStyle = "rgba(40,30,80,0.85)"; g.lineWidth = 3; g.lineCap = "round";
  for (let i = 0; i < 14; i++) {
    const x = R() * w, y = R() * h, r = 8 + R() * 14;
    g.beginPath(); for (let t = 0; t < Math.PI * 2.6; t += 0.2) g.lineTo(x + Math.cos(t) * r * (t / 8), y + Math.sin(t) * r * (t / 8)); g.stroke();
    g.beginPath(); g.moveTo(x, y); g.quadraticCurveTo(x + (R() - 0.5) * 60, y + 30, x + (R() - 0.5) * 40, y + 60); g.stroke();
  }
});
// The great serpent — slate scales, a violet sheen, the picture's blue-grey.
const serpentTex = () => canvasTex("plaza3d:vyen-serpent", 256, 256, (g, w, h) => {
  const R = mulberry32(0x5e12);
  g.fillStyle = "#3a4058"; g.fillRect(0, 0, w, h);
  for (let y = 0; y < h + 10; y += 11) for (let x = ((y / 11) | 0) % 2 ? 6 : 0; x < w + 6; x += 12) {
    g.fillStyle = `hsl(${228 + R() * 14}, ${18 + R() * 12}%, ${26 + R() * 12}%)`; g.beginPath(); g.arc(x, y, 7, 0, Math.PI); g.fill();
    g.strokeStyle = "rgba(190,170,240,0.28)"; g.lineWidth = 1; g.beginPath(); g.arc(x, y, 7, 0, Math.PI); g.stroke();
  }
});
const vestTex = () => canvasTex("plaza3d:willy-vest", 128, 128, (g, w, h) => {
  const R = mulberry32(0x9b); g.fillStyle = "#6b4a2a"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 300; i++) { g.fillStyle = `rgba(0,0,0,${R() * 0.18})`; g.fillRect(R() * w, R() * h, 3, 2); }
  g.fillStyle = "#4a3320"; for (let i = 0; i < 5; i++) { const x = R() * w, y = R() * h; g.beginPath(); g.arc(x, y, 4 + R() * 6, 0, Math.PI * 2); g.fill(); }
});
const maneTex = () => canvasTex("plaza3d:venus-mane", 128, 128, (g, w, h) => {
  const R = mulberry32(0x77a); g.fillStyle = "#6f7a3c"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 140; i++) { g.strokeStyle = R() < 0.5 ? "rgba(200,210,120,0.35)" : "rgba(40,50,20,0.4)"; g.lineWidth = 1 + R() * 1.6; g.beginPath(); const x = R() * w; g.moveTo(x, 0); g.bezierCurveTo(x + 8, h * 0.3, x - 8, h * 0.6, x + 3, h); g.stroke(); }
});
const coatTex = () => canvasTex("plaza3d:aen-coat", 256, 256, (g, w, h) => {
  const R = mulberry32(0x0c1); g.fillStyle = "#17151b"; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 700; i++) { g.fillStyle = `rgba(255,255,255,${R() * 0.045})`; g.fillRect(R() * w, R() * h, 1, 3); }
  // gold filigree down the lapels and along the hem — the picture's embroidery
  g.strokeStyle = "#c9a24c"; g.lineWidth = 2.2; g.lineCap = "round"; g.shadowColor = "#c9a24c"; g.shadowBlur = 2;
  for (const cx of [44, 84]) { g.beginPath(); g.moveTo(cx, 0); for (let y = 0; y < h; y += 8) g.lineTo(cx + Math.sin(y * 0.25) * 5, y); g.stroke(); for (let y = 12; y < h; y += 26) { g.beginPath(); g.arc(cx + 8, y, 5, Math.PI * 0.5, Math.PI * 1.9); g.stroke(); } }
  g.beginPath(); g.moveTo(0, h - 14); g.lineTo(w, h - 14); g.stroke(); g.beginPath(); g.moveTo(0, h - 8); g.lineTo(w, h - 8); g.stroke();
  for (let x = 6; x < w; x += 18) { g.beginPath(); g.arc(x, h - 22, 5, 0, Math.PI); g.stroke(); }
});

// ── shared limb helpers ──────────────────────────────────────────────
// An arm from the shoulder: upper capsule, a forearm group at the elbow
// (bent by `elbow`), a hand. `mats` = [upper, fore, hand]. `held` sits
// in the hand.
function Arm({ s, u, at, len = [0.14, 0.12], r = [0.034, 0.028, 0.03], mats, elbow = 0, elbowZ = 0, held = null, refCb, name }) {
  return (
    <group ref={refCb} name={name} position={at}>
      <mesh><sphereGeometry args={[u(r[0] * 1.15), 10, 8]} />{mats[0]}</mesh>
      <Cap r={u(r[0])} l={u(len[0])} position={[0, -u(len[0] / 2 + r[0] * 0.5), 0]} castShadow>{mats[0]}</Cap>
      <group position={[0, -u(len[0] + r[0]), 0]} rotation={[elbow, 0, elbowZ * s]}>
        <mesh><sphereGeometry args={[u(r[1] * 1.05), 8, 6]} />{mats[1]}</mesh>
        <Cap r={u(r[1])} l={u(len[1])} position={[0, -u(len[1] / 2 + r[1] * 0.5), 0]}>{mats[1]}</Cap>
        <group position={[0, -u(len[1] + r[1] * 1.3), 0]}>
          <mesh scale={[0.8, 1.25, 0.5]}><sphereGeometry args={[u(r[2]), 10, 8]} />{mats[2]}</mesh>
          {held}
        </group>
      </group>
    </group>
  );
}
function Leg({ s, u, at, len = [0.18, 0.16], r = [0.05, 0.04], mats, boot = null, name }) {
  return (
    <group name={name} position={at}>
      <Cap r={u(r[0])} l={u(len[0] - r[0])} position={[0, -u(len[0] / 2), 0]} castShadow>{mats[0]}</Cap>
      <mesh position={[0, -u(len[0]), 0]}><sphereGeometry args={[u(r[0] * 0.9), 10, 8]} />{mats[0]}</mesh>
      <Cap r={u(r[1])} l={u(len[1] - r[1])} position={[0, -u(len[0] + len[1] / 2), 0]} castShadow>{mats[1]}</Cap>
      {boot}
    </group>
  );
}
const useFacing = (ctl, root, rate = 9) => {
  const yaw = useRef(0);
  return (dt) => {
    const f = ctl.facing || { x: 0, y: 1 }; const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * rate); if (root.current) root.current.rotation.y = yaw.current;
  };
};

// ═══════════════════════════════════════════════════════════════════
// WILLY — the groundskeeper, off `willy.webp` and his bench.
// Short, stocky, a head like a mossy stone with two enormous sad brown
// eyes, a brown vest over a grey shirt, patched shorts, bare feet,
// pouches on the belt, and the open book. At the bench he mends a wire
// with a violet gem in it — the picture's own pose.
// ═══════════════════════════════════════════════════════════════════
// 1.4: short and stocky, but the bench comes to his belly, not his chin
export const WILLY_H = 1.4;
export function WillyBody({ ctl, h = WILLY_H }) {
  const root = useRef(); const head = useRef(); const armL = useRef(); const armR = useRef(); const gem = useRef();
  const ph = useRef(0); const facing = useFacing(ctl, root);
  const u = (v) => v * h;
  const skin = useMemo(() => skinTex("willy-moss", "#8a9448", "moss"), []);
  const vest = useMemo(() => vestTex(), []);
  const shorts = useMemo(() => clothTex("willy-shorts", "#5a4a36", { seed: 44, patches: true }), []);
  const face = useMemo(() => faceTex2("willy", {
    eye: "#7a4a22", eyeHot: "#f4d0a0", eyeRim: "#2a1206", lash: "#2e3a14", brow: "#4a5a2a", browW: 5, browArch: 2, browIn: -6, tilt: -0.05, look: 0,
    eyeW: 30, eyeH: 27, eyeY: 112, eyeGap: 48, mouth: "frown", lip: "#3a4a28", mouthY: 178,
  }), []);
  const skinMat = <meshStandardMaterial map={skin} roughness={0.9} />;
  const headR = u(0.16);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 2.2; facing(dt);
    const speed = ctl.speed || 0; ph.current += speed * dt * 8;
    if (root.current) root.current.position.y = speed ? Math.abs(Math.sin(ph.current)) * u(0.03) : 0;
    // he peers at the wire, tilts, blinks slowly by a nod; the gem breathes
    if (head.current) { head.current.rotation.x = 0.28 + Math.sin(t * 0.9) * 0.05; head.current.rotation.y = Math.sin(t * 0.35) * 0.12; }
    if (armL.current) { armL.current.rotation.x = -0.75 + Math.sin(t * 1.3) * 0.04; armL.current.rotation.z = 0.4; }
    if (armR.current) { armR.current.rotation.x = -0.85 + Math.sin(t * 2.1) * 0.08; armR.current.rotation.z = -0.45; }
    if (gem.current) gem.current.material.emissiveIntensity = 0.9 + Math.sin(t * 2.4) * 0.4;
  });
  const wireGem = (
    <group position={[0, -u(0.01), u(0.02)]}>
      <mesh rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[u(0.004), u(0.004), u(0.16), 5]} /><meshStandardMaterial color="#8a7a66" metalness={0.8} roughness={0.4} /></mesh>
      <mesh ref={gem} position={[u(0.04), 0, 0]}><octahedronGeometry args={[u(0.022), 0]} /><meshStandardMaterial color="#9a5cff" emissive="#9a5cff" emissiveIntensity={0.9} roughness={0.2} toneMapped={false} /></mesh>
    </group>
  );
  return (
    <group ref={root}>
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.26), 16]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      {/* stubby legs and BIG bare feet */}
      {[-1, 1].map((s) => (
        <group key={s} name={s < 0 ? "rig_legL" : "rig_legR"} position={[s * u(0.08), u(0.3), 0]}>
          <Cap r={u(0.06)} l={u(0.1)} position={[0, -u(0.1), 0]} castShadow><meshStandardMaterial map={shorts} roughness={0.95} /></Cap>
          <Cap r={u(0.05)} l={u(0.07)} position={[0, -u(0.2), 0]}>{skinMat}</Cap>
          <mesh position={[0, -u(0.27), u(0.05)]} scale={[1.1, 0.5, 2.0]}><sphereGeometry args={[u(0.06), 10, 8]} />{skinMat}</mesh>
          {[-1, 0, 1].map((k) => <mesh key={k} position={[k * u(0.035), -u(0.28), u(0.15)]}><sphereGeometry args={[u(0.02), 6, 5]} />{skinMat}</mesh>)}
        </group>
      ))}
      {/* the barrel of him: shorts, belt with buckle and pouches, grey shirt, the open vest */}
      <mesh position={[0, u(0.34), 0]} scale={[1.3, 0.8, 1.05]}><sphereGeometry args={[u(0.13), 14, 10]} /><meshStandardMaterial map={shorts} roughness={0.95} /></mesh>
      <mesh position={[0, u(0.4), 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[u(0.16), u(0.022), 6, 18]} /><meshStandardMaterial color="#4a3320" roughness={0.8} /></mesh>
      <mesh position={[0, u(0.4), u(0.165)]}><boxGeometry args={[u(0.05), u(0.04), u(0.012)]} /><meshStandardMaterial color="#b8b4a8" metalness={0.8} roughness={0.35} /></mesh>
      <mesh position={[-u(0.13), u(0.36), u(0.1)]} rotation={[0, 0.5, 0]}><boxGeometry args={[u(0.06), u(0.07), u(0.05)]} /><meshStandardMaterial color="#7a4a22" roughness={0.9} /></mesh>
      <mesh position={[u(0.13), u(0.35), u(0.1)]} rotation={[0, -0.5, 0]}><boxGeometry args={[u(0.07), u(0.08), u(0.05)]} /><meshStandardMaterial color="#3a8a8a" roughness={0.8} /></mesh>
      <mesh position={[u(0.13), u(0.36), u(0.13)]} rotation={[0, -0.5, 0]}><planeGeometry args={[u(0.04), u(0.04)]} /><meshBasicMaterial color="#7ce7c0" toneMapped={false} /></mesh>
      <mesh position={[0, u(0.52), 0]} scale={[1.35, 1.1, 1.0]} castShadow><sphereGeometry args={[u(0.135), 14, 12]} /><meshStandardMaterial color="#5e625c" roughness={0.95} /></mesh>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * u(0.1), u(0.52), u(0.04)]} rotation={[0, s * 0.35, 0]} scale={[0.5, 1.15, 0.95]}><sphereGeometry args={[u(0.145), 12, 10, Math.PI * 0.1, Math.PI * 0.8]} /><meshStandardMaterial map={vest} roughness={0.95} side={THREE.DoubleSide} /></mesh>
      ))}
      <mesh position={[0, u(0.52), -u(0.06)]} scale={[1.42, 1.12, 0.9]}><sphereGeometry args={[u(0.135), 14, 12, Math.PI * 0.85, Math.PI * 1.3]} /><meshStandardMaterial map={vest} roughness={0.95} side={THREE.DoubleSide} /></mesh>
      {/* arms — sleeves rolled, mossy forearms, the book and the wire */}
      <Arm s={-1} u={u} at={[-u(0.17), u(0.6), 0]} len={[0.12, 0.11]} r={[0.045, 0.04, 0.042]} mats={[<meshStandardMaterial color="#5e625c" roughness={0.95} />, skinMat, skinMat]} elbow={-0.4} refCb={armL} name="rig_armL" held={<group position={[0, -u(0.01), u(0.03)]} rotation={[0.6, 0.4, 0]} scale={h * 0.75}><BookProp /></group>} />
      <Arm s={1} u={u} at={[u(0.17), u(0.6), 0]} len={[0.12, 0.11]} r={[0.045, 0.04, 0.042]} mats={[<meshStandardMaterial color="#5e625c" roughness={0.95} />, skinMat, skinMat]} elbow={-0.3} refCb={armR} name="rig_armR" held={wireGem} />
      {/* THE HEAD — big, low, a mossy stone with a wide face */}
      <group ref={head} name="rig_head" position={[0, u(0.66), u(0.02)]}>
        <mesh position={[0, headR * 0.95, 0]} scale={[1.08, 0.98, 1.0]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 0.95, 0]} scale={[1.08, 0.98, 1.0]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.26, Math.PI * 0.58]} />
          <meshStandardMaterial map={face} transparent roughness={0.85} />
        </mesh>
        {/* the flat wide muzzle and the moss on top */}
        {/* a small flat nose; the wide frown is painted below it */}
        <mesh position={[0, headR * 0.78, headR * 0.92]} scale={[1.4, 0.5, 0.5]}><sphereGeometry args={[headR * 0.16, 10, 8]} />{skinMat}</mesh>
        {/* moss on the crown — three patches, not a beret */}
        {[[0.1, 1.85, -0.35, 0.7, 0.5], [-0.5, 1.7, 0.1, 0.55, 0.45], [0.55, 1.65, 0.25, 0.5, 0.4]].map((m, i) => (
          <mesh key={i} position={[m[0] * headR, m[1] * headR, m[2] * headR]} scale={[m[3], m[4] * 0.55, m[3]]}><sphereGeometry args={[headR * 0.9, 12, 8]} /><meshStandardMaterial color="#6a8a3a" roughness={1} /></mesh>
        ))}
        {[[0, 2.0, 0.1, 0], [0.55, 1.85, -0.2, 0.5], [-0.6, 1.8, 0.1, -0.5], [0.25, 1.9, -0.6, 0.2], [-0.25, 1.95, 0.5, -0.1], [0.7, 1.55, 0.45, 0.7], [-0.75, 1.5, 0.4, -0.7]].map((c, i) => (
          <mesh key={i} position={[c[0] * headR, c[1] * headR, c[2] * headR]} rotation={[c[2] * 0.6, 0, -c[3]]}><coneGeometry args={[headR * 0.13, headR * 0.42, 5]} /><meshStandardMaterial color="#6a8a3a" roughness={1} /></mesh>
        ))}
        {/* a mossy chin beard */}
        <mesh position={[0, headR * 0.28, headR * 0.7]} scale={[1.3, 0.5, 0.6]}><sphereGeometry args={[headR * 0.4, 10, 8]} /><meshStandardMaterial color="#5d7a33" roughness={1} /></mesh>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// V'YEN — Sweet Treats, off her corner of `plaza_1.webp`.
// Grey-violet skin inked with dark swirls, a wild white mane, red eyes,
// a gold circlet with its red jewel, gold at the neck and wrists, a
// purple wrap top — and below the waist she is the coil: slate scales
// set with gems, sweeping down and to the left. She writes in the
// ledger and never stops.
// ═══════════════════════════════════════════════════════════════════
// 2.2, not a person's 1.3: in the painting she rises off the coil to
// the height of the sign; the counter is at her hips, not her chin.
export const VYEN_H = 2.2417;   // her sculpt's measured crown (vyenModel3D MODEL_TOP): the glb is in Plaza metres, drawn at scale 1 (2026-09-23)
export function VyenBody({ ctl, h = VYEN_H }) {
  const root = useRef(); const head = useRef(); const armR = useRef(); const armL = useRef(); const hairBack = useRef(); const chest = useRef();
  const facing = useFacing(ctl, root, 6);
  const u = (v) => v * h;
  const inkSkin = useMemo(() => tattooSkinTex(), []);
  const scales = useMemo(() => { const t = serpentTex(); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(2, 2); return t; }, []);
  const hair = useMemo(() => hairTex("vyen", "#f2eff5", "rgba(255,255,255,0.3)"), []);
  const face = useMemo(() => faceTex2("vyen", {
    eye: "#e0304a", eyeHot: "#ffd0d8", eyeRim: "#4a0a18", lash: "#2a1a2e", brow: "#e8e2ee", browW: 3, browArch: 12, browIn: 4, tilt: 0.12, look: 1, slit: true,
    eyeW: 30, eyeH: 20, eyeY: 122, eyeGap: 44, mouth: "smirk", lip: "#7a2a4a", mouthY: 194, blush: "rgba(150,80,200,0.25)",
    marks: [[-40, 100], [40, 100]], markColor: "#c0203a",
  }), []);
  const skinMat = <meshStandardMaterial color="#9d93b4" roughness={0.6} />;
  const inkMat = <meshStandardMaterial map={inkSkin} roughness={0.6} />;
  const hairMat = <meshStandardMaterial map={hair} roughness={0.7} />;
  const goldMat = <meshStandardMaterial color="#d8a840" metalness={0.9} roughness={0.28} emissive="#5a3a08" emissiveIntensity={0.25} />;
  const topMat = <meshStandardMaterial color="#4a2e7a" roughness={0.85} />;
  const headR = u(0.075);
  const coil = useMemo(() => {
    // dense enough that the beads overlap into one body — a coil that
    // reads as pearls is not a serpent (the BeadChain law, by hand)
    const out = []; const n = 230;
    for (let i = 0; i <= n; i++) {
      const t = i / n; const a = Math.PI * 0.85 + t * Math.PI * 2 * 1.75; const r = 0.3 + t * 3.9;
      const rr = u(0.15 - t * 0.118);
      out.push({ p: [Math.cos(a) * r * 0.9 - t * 1.3, Math.max(rr * 0.9, 0.5 - t * 0.28 + Math.sin(t * Math.PI * 3) * 0.14), Math.sin(a) * r * 0.55 + t * 1.0], r: rr });
    }
    return out;
  }, [h]);
  const coilGeo = useMemo(() => mergeBeads(coil), [coil]);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 0.7; facing(dt);
    const br = 1 + Math.sin(t * 1.4) * 0.012; if (chest.current) chest.current.scale.set(1.1 / Math.sqrt(br), 1.25 * br, 0.8);
    if (head.current) { head.current.rotation.x = 0.3 + Math.sin(t * 0.7) * 0.03; head.current.rotation.y = Math.sin(t * 0.45) * 0.2; }
    if (armR.current) { armR.current.rotation.x = -0.45 + Math.sin(t * 5) * 0.04; armR.current.rotation.z = -0.3 + Math.sin(t * 5) * 0.05; }
    if (armL.current) { armL.current.rotation.x = -0.4; armL.current.rotation.z = 0.4; }
    if (hairBack.current) hairBack.current.rotation.x = Math.sin(t * 0.9) * 0.04;
  });
  return (
    <group ref={root}>
      {/* THE COIL */}
      <mesh geometry={coilGeo} castShadow><meshStandardMaterial map={scales} roughness={0.5} metalness={0.15} /></mesh>
      {coil.map((b, i) => (
        <group key={i} position={b.p}>
          {i % 16 === 8 && i < coil.length - 12 && (
            <mesh position={[0, b.r * 0.82, 0]} rotation={[0.3, i, 0]}><octahedronGeometry args={[b.r * 0.32, 0]} /><meshStandardMaterial color={i % 10 === 2 ? "#e8453c" : "#B388FF"} emissive={i % 10 === 2 ? "#e8453c" : "#B388FF"} emissiveIntensity={0.9} roughness={0.2} toneMapped={false} /></mesh>
          )}
        </group>
      ))}
      <mesh position={coil[coil.length - 1].p} rotation={[0, 0.6, 0]} scale={[1, 0.5, 2.4]}><sphereGeometry args={[u(0.05), 8, 6]} /><meshStandardMaterial map={scales} roughness={0.5} /></mesh>
      {/* the rise from the coil into her hips */}
      <mesh position={[0, u(0.4), 0]} scale={[1.1, 1.3, 1.0]}><sphereGeometry args={[u(0.13), 14, 10]} /><meshStandardMaterial map={scales} roughness={0.5} /></mesh>
      <mesh position={[0, u(0.53), 0]} scale={[1.0, 0.6, 0.85]}><sphereGeometry args={[u(0.11), 12, 10]} />{skinMat}</mesh>
      <mesh position={[0, u(0.5), 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[u(0.1), u(0.02), 6, 18]} />{goldMat}</mesh>
      {/* the waist (inked) and the wrap top */}
      <mesh position={[0, u(0.585), 0]} scale={[0.9, 0.9, 0.72]}><sphereGeometry args={[u(0.085), 14, 10]} />{inkMat}</mesh>
      <mesh ref={chest} position={[0, u(0.665), 0]} scale={[1.1, 1.25, 0.8]} castShadow><sphereGeometry args={[u(0.095), 16, 12]} />{topMat}</mesh>
      {[-1, 1].map((s) => <mesh key={s} position={[s * u(0.045), u(0.72), u(0.04)]} rotation={[0.3, 0, -s * 0.55]}><cylinderGeometry args={[u(0.012), u(0.012), u(0.11), 6]} />{topMat}</mesh>)}
      <mesh position={[0, u(0.755), 0]} rotation={[Math.PI / 2 + 0.25, 0, 0]}><torusGeometry args={[u(0.06), u(0.008), 5, 18]} />{goldMat}</mesh>
      {[-1, 0, 1].map((k) => <mesh key={k} position={[k * u(0.035), u(0.7) - Math.abs(k) * u(0.01), u(0.095)]}><octahedronGeometry args={[u(0.014), 0]} /><meshStandardMaterial color="#e8453c" emissive="#e8453c" emissiveIntensity={0.9} roughness={0.2} toneMapped={false} /></mesh>)}
      {/* arms — inked, gold cuffs, the pen hand */}
      {/* gold cuffs ride in the hand group so they follow the wrists */}
      <Arm s={-1} u={u} at={[-u(0.135), u(0.74), 0]} len={[0.13, 0.12]} r={[0.03, 0.026, 0.028]} mats={[inkMat, inkMat, skinMat]} elbow={-0.9} refCb={armL} name="rig_armL"
        held={<mesh position={[0, u(0.03), 0]}><torusGeometry args={[u(0.03), u(0.007), 6, 14]} />{goldMat}</mesh>} />
      <Arm s={1} u={u} at={[u(0.135), u(0.74), 0]} len={[0.13, 0.12]} r={[0.03, 0.026, 0.028]} mats={[inkMat, inkMat, skinMat]} elbow={-1.1} refCb={armR} name="rig_armR"
        held={<group><mesh position={[0, u(0.03), 0]}><torusGeometry args={[u(0.03), u(0.007), 6, 14]} />{goldMat}</mesh><mesh position={[0, -u(0.01), u(0.03)]} rotation={[1.1, 0, 0.3]}><cylinderGeometry args={[u(0.004), u(0.006), u(0.1), 5]} /><meshStandardMaterial color="#2a2030" /></mesh></group>} />
      {/* HEAD */}
      <group ref={head} name="rig_head" position={[0, u(0.8), 0]}>
        <mesh position={[0, u(0.012), 0]}><cylinderGeometry args={[u(0.028), u(0.034), u(0.06), 10]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.94, 1.1, 0.96]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.94, 1.1, 0.96]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.24, Math.PI * 0.6]} />
          <meshStandardMaterial map={face} transparent roughness={0.62} />
        </mesh>
        {/* the circlet and its jewel, the pointed ears */}
        <mesh position={[0, headR * 1.45, 0]} rotation={[Math.PI / 2 + 0.15, 0, 0]}><torusGeometry args={[headR * 0.96, headR * 0.05, 6, 24]} />{goldMat}</mesh>
        <mesh position={[0, headR * 1.42, headR * 0.98]}><octahedronGeometry args={[headR * 0.1, 0]} /><meshStandardMaterial color="#e8453c" emissive="#e8453c" emissiveIntensity={1.0} roughness={0.2} toneMapped={false} /></mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * headR * 1.0, headR * 1.0, -headR * 0.05]} rotation={[0, 0, s * 0.9]} scale={[0.5, 1, 0.6]}><coneGeometry args={[headR * 0.22, headR * 0.5, 6]} />{skinMat}</mesh>)}
        {/* the wild white mane: cap, a swept part, and heavy falls each side and behind */}
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.1, 1.0, 1.12]}><sphereGeometry args={[headR * 1.05, 20, 14, Math.PI * 0.9, Math.PI * 1.2, 0, Math.PI * 0.62]} />{hairMat}</mesh>
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.1, 1.0, 1.12]}><sphereGeometry args={[headR * 1.05, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.24]} />{hairMat}</mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.45, headR * 1.7, headR * 0.6]} rotation={[0.5, s * 0.4, -s * 0.8]} scale={[1.5, 0.4, 0.7]}><sphereGeometry args={[headR * 0.42, 10, 8]} />{hairMat}</mesh>)}
        <group ref={hairBack} position={[0, headR * 1.2, -headR * 0.5]}>
          <mesh position={[0, -u(0.16), -headR * 0.2]} scale={[1.9, 1, 0.7]}><capsuleGeometry args={[headR * 0.6, u(0.28), 4, 12]} />{hairMat}</mesh>
        </group>
        {[-1, 1].map((s) => [0, 1].map((i) => (
          <Lock key={s + "" + i} from={[s * headR * (0.85 + i * 0.2), headR * (1.35 - i * 0.15), headR * (0.3 - i * 0.5)]} to={[s * headR * (1.7 + i * 0.5), -u(0.2) + i * u(0.05), headR * (0.7 - i * 0.5)]} waves={2 + i * 0.7} amp={headR * (0.32 + i * 0.08)} side={i % 2 ? -s : s} r0={headR * 0.2} r1={headR * 0.08} mat={hairMat} bScale={[1.9, 1, 0.55]} />
        )))}
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VENUS — Card Packs, off her corner of `plaza_1.webp`.
// The lioness: tawny, an olive mane with a red bloom in it, round ears
// with tufts, glasses, a sleeveless olive-cream dress with a gold cross,
// the tail — and two packs held up beside her head, always, because
// that is what she is doing in the painting.
// ═══════════════════════════════════════════════════════════════════
// 1.6: she stands a head over the kiosk's glass case, as painted
export const VENUS_H = 1.6;
export function VenusBody({ ctl, h = VENUS_H }) {
  const root = useRef(); const head = useRef(); const armL = useRef(); const armR = useRef(); const tail = useRef(); const chest = useRef();
  const ph = useRef(0); const facing = useFacing(ctl, root);
  const u = (v) => v * h;
  const mane = useMemo(() => maneTex(), []);
  const dress = useMemo(() => clothTex("venus-dress2", "#b3ab74", { seed: 5 }), []);
  const face = useMemo(() => faceTex2("venus", {
    eye: "#7aa83a", eyeHot: "#f4ffd0", eyeRim: "#1e3a10", lash: "#3a2a1a", brow: "#5a4a2a", browW: 4, browArch: 10, browIn: 2, tilt: 0.1, look: 1, slit: true,
    eyeW: 30, eyeH: 21, eyeY: 122, eyeGap: 44, mouth: "smirk", lip: "#8a4a4a", mouthY: 194, blush: "rgba(200,110,90,0.22)",
  }), []);
  const glassesMat = <meshStandardMaterial color="#e9d9b5" metalness={0.8} roughness={0.3} />;
  const SKIN = "#c9a06a";
  const skinMat = <meshStandardMaterial color={SKIN} emissive="#3a2010" emissiveIntensity={0.2} roughness={0.65} />;
  const maneMat = <meshStandardMaterial map={mane} roughness={0.9} />;
  const dressMat = <meshStandardMaterial map={dress} roughness={0.9} />;
  const headR = u(0.072);
  const Pack = ({ c }) => <mesh position={[0, u(0.04), u(0.02)]} rotation={[0.1, 0, 0]}><boxGeometry args={[u(0.075), u(0.1), u(0.014)]} /><meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.3} roughness={0.4} /></mesh>;
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 3.1; facing(dt);
    const speed = ctl.speed || 0; ph.current += speed * dt * 9;
    if (root.current) root.current.position.y = speed ? Math.abs(Math.sin(ph.current)) * u(0.02) : 0;
    const br = 1 + Math.sin(t * 1.5) * 0.012; if (chest.current) chest.current.scale.set(1.12 / Math.sqrt(br), 1.2 * br, 0.8);
    if (head.current) { head.current.rotation.y = Math.sin(t * 0.6) * 0.18; head.current.rotation.z = Math.sin(t * 0.4) * 0.06; }
    // both packs up beside the head, a little bounce — "buy a pack!"
    const b = Math.sin(t * 2.2) * 0.06;
    if (armL.current) { armL.current.rotation.x = -0.25 + b; armL.current.rotation.z = 0.7; }
    if (armR.current) { armR.current.rotation.x = -0.25 - b; armR.current.rotation.z = -0.7; }
    if (tail.current) { tail.current.rotation.y = Math.sin(t * 1.5) * 0.6; tail.current.rotation.x = Math.sin(t * 2.2) * 0.2; }
  });
  return (
    <group ref={root}>
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.2), 16]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      {[-1, 1].map((s) => (
        <Leg key={s} s={s} u={u} at={[s * u(0.05), u(0.46), u(0.01)]} len={[0.2, 0.2]} r={[0.05, 0.04]} mats={[skinMat, skinMat]} name={s < 0 ? "rig_legL" : "rig_legR"}
          boot={<><Cap r={u(0.044)} l={u(0.04)} position={[0, -u(0.41), 0]}><meshStandardMaterial color="#5a3a22" roughness={0.6} /></Cap><mesh position={[0, -u(0.44), u(0.03)]} scale={[1, 0.55, 1.6]}><sphereGeometry args={[u(0.048), 10, 8]} /><meshStandardMaterial color="#5a3a22" roughness={0.6} /></mesh></>} />
      ))}
      {/* the dress: a fitted sleeveless sheath to the knee, V-necked, a gold cross */}
      <mesh position={[0, u(0.47), 0]} scale={[1.1, 0.6, 0.85]}><sphereGeometry args={[u(0.1), 14, 10]} />{dressMat}</mesh>
      <mesh position={[0, u(0.36), 0]} castShadow><cylinderGeometry args={[u(0.1), u(0.115), u(0.24), 14, 1, true]} /><meshStandardMaterial map={dress} roughness={0.9} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, u(0.555), 0]} scale={[0.9, 0.9, 0.72]}><sphereGeometry args={[u(0.082), 14, 10]} />{dressMat}</mesh>
      <mesh ref={chest} position={[0, u(0.65), 0]} scale={[1.12, 1.2, 0.8]} castShadow><sphereGeometry args={[u(0.095), 16, 12]} />{dressMat}</mesh>
      {/* the deep V: skin showing from the collarbones to the sternum */}
      <mesh position={[0, u(0.69), u(0.078)]} rotation={[0.18, 0, 0]}><circleGeometry args={[u(0.06), 3, Math.PI * 1.5, Math.PI * 2]} />{skinMat}</mesh>
      <mesh position={[0, u(0.7), u(0.077)]} rotation={[0.18, 0, Math.PI]} scale={[1.2, 1.4, 1]}><circleGeometry args={[u(0.05), 3]} />{skinMat}</mesh>
      <mesh position={[0, u(0.69), u(0.088)]}><boxGeometry args={[u(0.006), u(0.036), u(0.004)]} /><meshStandardMaterial color="#d8a840" metalness={0.9} roughness={0.3} /></mesh>
      <mesh position={[0, u(0.698), u(0.088)]}><boxGeometry args={[u(0.022), u(0.006), u(0.004)]} /><meshStandardMaterial color="#d8a840" metalness={0.9} roughness={0.3} /></mesh>
      {/* bare arms, the packs held up */}
      <Arm s={-1} u={u} at={[-u(0.12), u(0.72), 0]} len={[0.13, 0.11]} r={[0.03, 0.026, 0.028]} mats={[skinMat, skinMat, skinMat]} elbow={-2.7} elbowZ={0.1} refCb={armL} name="rig_armL" held={<Pack c="#7c3a9c" />} />
      <Arm s={1} u={u} at={[u(0.12), u(0.72), 0]} len={[0.13, 0.11]} r={[0.03, 0.026, 0.028]} mats={[skinMat, skinMat, skinMat]} elbow={-2.7} elbowZ={0.1} refCb={armR} name="rig_armR" held={<Pack c="#c9a24c" />} />
      {/* the tail */}
      <group ref={tail} name="rig_tail" position={[0, u(0.45), -u(0.09)]}>
        <BeadChain pts={[[0, 0, 0], [u(0.03), -u(0.06), -u(0.09)], [u(0.1), -u(0.16), -u(0.14)], [u(0.19), -u(0.26), -u(0.1)], [u(0.24), -u(0.33), -u(0.02)], [u(0.23), -u(0.38), u(0.04)]]} r0={u(0.011)} r1={u(0.007)} mat={skinMat} />
        <mesh position={[u(0.225), -u(0.41), u(0.06)]} rotation={[0.5, 0, 0.2]} scale={[1, 1.7, 1]}><sphereGeometry args={[u(0.024), 8, 6]} /><meshStandardMaterial color="#5a3a1a" roughness={1} /></mesh>
      </group>
      {/* HEAD */}
      <group ref={head} name="rig_head" position={[0, u(0.79), 0]}>
        <mesh position={[0, u(0.012), 0]}><cylinderGeometry args={[u(0.028), u(0.034), u(0.06), 10]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.96, 1.08, 0.96]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.96, 1.08, 0.96]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.24, Math.PI * 0.6]} />
          <meshStandardMaterial map={face} transparent roughness={0.62} />
        </mesh>
        {/* a small lion muzzle, the glasses */}
        <mesh position={[0, headR * 0.72, headR * 0.86]} scale={[1.1, 0.7, 0.7]}><sphereGeometry args={[headR * 0.3, 10, 8]} />{skinMat}</mesh>
        <mesh position={[0, headR * 0.85, headR * 1.05]}><sphereGeometry args={[headR * 0.08, 8, 6]} /><meshStandardMaterial color="#3a2418" /></mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.36, headR * 1.12, headR * 0.93]} rotation={[0.15, 0, 0]}><torusGeometry args={[headR * 0.25, headR * 0.02, 6, 16]} />{glassesMat}</mesh>)}
        <mesh position={[0, headR * 1.12, headR * 0.98]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[headR * 0.015, headR * 0.015, headR * 0.22, 4]} />{glassesMat}</mesh>
        {/* round ears with tufts, the mane all round and behind, the red bloom */}
        {[-1, 1].map((s) => (
          <group key={s} position={[s * headR * 0.85, headR * 1.9, -headR * 0.1]}>
            <mesh scale={[1, 1, 0.6]}><sphereGeometry args={[headR * 0.38, 10, 8]} />{skinMat}</mesh>
            <mesh position={[0, 0, headR * 0.16]} scale={[1, 1, 0.5]}><sphereGeometry args={[headR * 0.24, 8, 6]} /><meshStandardMaterial color="#e0b890" roughness={0.8} /></mesh>
            <mesh position={[0, headR * 0.3, 0]}><coneGeometry args={[headR * 0.14, headR * 0.34, 5]} />{maneMat}</mesh>
          </group>
        ))}
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.12, 1.0, 1.12]}><sphereGeometry args={[headR * 1.05, 20, 14, Math.PI * 0.9, Math.PI * 1.2, 0, Math.PI * 0.62]} />{maneMat}</mesh>
        <mesh position={[0, headR * 1.1, -headR * 0.06]} scale={[1.12, 1.0, 1.12]}><sphereGeometry args={[headR * 1.05, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.26]} />{maneMat}</mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * headR * 0.55, headR * 1.95, headR * 0.5]} rotation={[0.6, s * 0.4, -s * 0.8]} scale={[1.5, 0.4, 0.7]}><sphereGeometry args={[headR * 0.4, 10, 8]} />{maneMat}</mesh>)}
        {/* THE MANE — a full ring of volume around the face, cheek to cheek, spilling past the shoulders */}
        {Array.from({ length: 14 }, (_, i) => { const a = (i / 14) * Math.PI * 1.7 + Math.PI * 0.15, r = headR * 1.3; return <mesh key={i} position={[Math.cos(a) * r, headR * 0.7 + Math.sin(a) * headR * 0.55, -Math.abs(Math.sin(a)) * r * 0.45 - headR * 0.1]} scale={[1, 1.5, 0.9]}><sphereGeometry args={[headR * 0.5, 9, 7]} />{maneMat}</mesh>; })}
        {[-1, 1].map((s) => [0, 1].map((i) => <Lock key={s + "" + i} from={[s * headR * (1.1 + i * 0.3), headR * (1.1 - i * 0.2), headR * (0.4 - i * 0.5)]} to={[s * headR * (1.6 + i * 0.4), -u(0.16) + i * u(0.03), headR * (0.7 - i * 0.5)]} waves={2.2 + i * 0.5} amp={headR * 0.3} side={i % 2 ? -s : s} r0={headR * 0.26} r1={headR * 0.1} mat={maneMat} bScale={[1.7, 1, 0.6]} />))}
        <group position={[headR * 0.75, headR * 1.85, headR * 0.45]}>
          {[0, 1, 2, 3, 4].map((i) => <mesh key={i} position={[Math.cos(i * 1.257) * headR * 0.12, Math.sin(i * 1.257) * headR * 0.12, 0]}><sphereGeometry args={[headR * 0.09, 8, 6]} /><meshStandardMaterial color="#c8203a" roughness={0.6} /></mesh>)}
          <mesh position={[0, 0, headR * 0.05]}><sphereGeometry args={[headR * 0.06, 6, 5]} /><meshStandardMaterial color="#f0c040" /></mesh>
        </group>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE AENARCHIST (M) — off `player_m_down_a.webp`.
// White hair swept up and back, the dark beard, green eyes, a black
// long coat embroidered in gold over a white collared shirt, a black
// sash, boots — and his hands in his pockets, which is the whole pose.
// ═══════════════════════════════════════════════════════════════════
export const AEN_M_H = VENUS_H;   // caP'n 2026-09-23: Laostre is "the same height as Venus, no taller than her" (was PERSON_H 1.3)
export function AenarchistMBody({ ctl, h = AEN_M_H }) {
  const root = useRef(); const head = useRef(); const chest = useRef(); const armL = useRef(); const armR = useRef();
  const ph = useRef(0); const facing = useFacing(ctl, root, 11);
  const u = (v) => v * h;
  const coat = useMemo(() => coatTex(), []);
  const hair = useMemo(() => hairTex("aen-m", "#f1ede8", "rgba(255,255,255,0.35)"), []);
  const face = useMemo(() => faceTex2("aen-m", {
    eye: "#3a9a4a", eyeHot: "#d8ffd8", eyeRim: "#0e2a12", lash: "#2a1a10", brow: "#e8e2da", browW: 5, browArch: 6, browIn: 6, tilt: 0.08, look: 1,
    eyeW: 26, eyeH: 16, eyeY: 120, eyeGap: 44, mouth: "flat", lip: "#6a3a3a", mouthY: 186,
  }), []);
  const SKIN = "#c58f66";
  const skinMat = <meshStandardMaterial color={SKIN} emissive="#3a1e10" emissiveIntensity={0.2} roughness={0.62} />;
  const coatMat = <meshStandardMaterial map={coat} roughness={0.9} />;
  const hairMat = <meshStandardMaterial map={hair} roughness={0.7} />;
  const beardMat = <meshStandardMaterial color="#26201e" roughness={0.95} />;
  const headR = u(0.075);
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime(); facing(dt);
    const speed = ctl.speed || 0; ph.current += speed * dt * 9.5;
    if (root.current) root.current.position.y = speed ? Math.abs(Math.sin(ph.current)) * u(0.025) : 0;
    const br = 1 + Math.sin(t * 1.5) * 0.012; if (chest.current) chest.current.scale.set(1.25 / Math.sqrt(br), 1.25 * br, 0.85);
    if (head.current) { head.current.rotation.y = Math.sin(t * 0.55) * 0.18; head.current.rotation.x = Math.sin(t * 0.8) * 0.02; }
    // hands stay in the pockets; walking rocks the shoulders instead
    const sw = speed ? Math.sin(ph.current) * 0.12 : 0;
    if (armL.current) { armL.current.rotation.x = 0.05 + sw; armL.current.rotation.z = 0.2; }
    if (armR.current) { armR.current.rotation.x = 0.05 - sw; armR.current.rotation.z = -0.2; }
  });
  const boot = <><Cap r={u(0.046)} l={u(0.07)} position={[0, -u(0.4), 0]}><meshStandardMaterial color="#3a2a22" roughness={0.6} /></Cap><mesh position={[0, -u(0.445), u(0.035)]} scale={[1.05, 0.55, 1.7]}><sphereGeometry args={[u(0.05), 10, 8]} /><meshStandardMaterial color="#2e2119" roughness={0.6} /></mesh></>;
  return (
    <group ref={root}>
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[u(0.22), 16]} /><meshBasicMaterial color="#000" transparent opacity={0.35} depthWrite={false} /></mesh>
      {[-1, 1].map((s) => <Leg key={s} s={s} u={u} at={[s * u(0.06), u(0.46), 0]} len={[0.2, 0.2]} r={[0.052, 0.044]} mats={[<meshStandardMaterial color="#1e1b22" roughness={0.9} />, <meshStandardMaterial color="#1e1b22" roughness={0.9} />]} boot={boot} name={s < 0 ? "rig_legL" : "rig_legR"} />)}
      {/* THE COAT: a long skirt from the hips, open at the front; the sash; the torso; the wide lapels; the white shirt in the V */}
      <mesh position={[0, u(0.3), 0]} castShadow>
        {/* θ=0 is +z (the front): the gap is centred there, and the trousers show through it */}
        <cylinderGeometry args={[u(0.13), u(0.17), u(0.36), 16, 1, true, Math.PI * 0.14, Math.PI * 1.72]} />
        <meshStandardMaterial map={coat} roughness={0.9} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, u(0.48), 0]} scale={[1.2, 0.65, 0.9]}><sphereGeometry args={[u(0.11), 14, 10]} />{coatMat}</mesh>
      <mesh position={[0, u(0.5), 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[u(0.125), u(0.022), 6, 18]} /><meshStandardMaterial color="#26222c" roughness={0.9} /></mesh>
      <mesh ref={chest} position={[0, u(0.64), 0]} scale={[1.25, 1.25, 0.85]} castShadow><sphereGeometry args={[u(0.1), 16, 12]} />{coatMat}</mesh>
      <mesh position={[0, u(0.65), u(0.075)]} rotation={[0.1, 0, 0]}><planeGeometry args={[u(0.075), u(0.2)]} /><meshStandardMaterial color="#f4f1ea" roughness={0.8} /></mesh>
      <mesh position={[0, u(0.745), 0]}><cylinderGeometry args={[u(0.042), u(0.05), u(0.04), 12, 1, true]} /><meshStandardMaterial color="#f4f1ea" roughness={0.8} side={THREE.DoubleSide} /></mesh>
      {[-1, 1].map((s) => <mesh key={s} position={[s * u(0.062), u(0.68), u(0.085)]} rotation={[0.15, s * 0.5, s * 0.25]}><planeGeometry args={[u(0.07), u(0.16)]} /><meshStandardMaterial map={coat} roughness={0.9} side={THREE.DoubleSide} /></mesh>)}
      {/* the arms, bent into the pockets */}
      <Arm s={-1} u={u} at={[-u(0.14), u(0.72), 0]} len={[0.14, 0.1]} r={[0.04, 0.036, 0.03]} mats={[coatMat, coatMat, skinMat]} elbow={-0.5} elbowZ={-0.75} refCb={armL} name="rig_armL" />
      <Arm s={1} u={u} at={[u(0.14), u(0.72), 0]} len={[0.14, 0.1]} r={[0.04, 0.036, 0.03]} mats={[coatMat, coatMat, skinMat]} elbow={-0.5} elbowZ={-0.75} refCb={armR} name="rig_armR" />
      {/* HEAD */}
      <group ref={head} name="rig_head" position={[0, u(0.79), 0]}>
        <mesh position={[0, u(0.012), 0]}><cylinderGeometry args={[u(0.03), u(0.036), u(0.06), 10]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.96, 1.08, 0.96]} castShadow><sphereGeometry args={[headR, 20, 16]} />{skinMat}</mesh>
        <mesh position={[0, headR * 1.05, 0]} scale={[0.96, 1.08, 0.96]}>
          <sphereGeometry args={[headR * 1.012, 24, 18, Math.PI * 0.2, Math.PI * 0.6, Math.PI * 0.24, Math.PI * 0.6]} />
          <meshStandardMaterial map={face} transparent roughness={0.62} />
        </mesh>
        {/* the beard: a dark jaw shell under the mouth, and the moustache */}
        <mesh position={[0, headR * 0.72, headR * 0.02]} scale={[0.97, 0.9, 0.98]}><sphereGeometry args={[headR * 1.0, 16, 12, Math.PI * 0.1, Math.PI * 0.8, Math.PI * 0.6, Math.PI * 0.3]} />{beardMat}</mesh>
        <mesh position={[0, headR * 0.35, headR * 0.35]} scale={[0.8, 0.5, 0.8]}><sphereGeometry args={[headR * 0.7, 12, 10]} />{beardMat}</mesh>
        <mesh position={[0, headR * 0.7, headR * 0.92]} scale={[1.6, 0.35, 0.5]}><sphereGeometry args={[headR * 0.22, 10, 8]} />{beardMat}</mesh>
        {/* ears with the small rings */}
        {[-1, 1].map((s) => <group key={s} position={[s * headR * 0.96, headR * 0.95, 0]}><mesh scale={[0.5, 1, 0.8]}><sphereGeometry args={[headR * 0.22, 8, 6]} />{skinMat}</mesh><mesh position={[0, -headR * 0.2, 0]} rotation={[0, Math.PI / 2, 0]}><torusGeometry args={[headR * 0.06, headR * 0.015, 5, 10]} /><meshStandardMaterial color="#d8dde6" metalness={0.9} roughness={0.25} /></mesh></group>)}
        {/* HAIR: swept up and back — a cap, a big wave over the crown leaning right, shorter at the sides */}
        <mesh position={[0, headR * 1.15, -headR * 0.08]} scale={[1.08, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, Math.PI * 0.85, Math.PI * 1.3, 0, Math.PI * 0.5]} />{hairMat}</mesh>
        <mesh position={[0, headR * 1.15, -headR * 0.08]} scale={[1.08, 1.0, 1.1]}><sphereGeometry args={[headR * 1.04, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.3]} />{hairMat}</mesh>
        {/* the pompadour: a tall wave over the crown leaning right, a second behind it, a curl at the temple */}
        <mesh position={[headR * 0.2, headR * 2.25, headR * 0.2]} rotation={[0.35, 0.2, -0.5]} scale={[1.5, 1.0, 1.2]}><sphereGeometry args={[headR * 0.62, 12, 10]} />{hairMat}</mesh>
        <mesh position={[-headR * 0.35, headR * 2.15, -headR * 0.2]} rotation={[-0.2, 0, 0.5]} scale={[1.3, 0.9, 1.1]}><sphereGeometry args={[headR * 0.55, 12, 10]} />{hairMat}</mesh>
        <mesh position={[-headR * 0.6, headR * 1.85, headR * 0.5]} rotation={[0.6, -0.3, 0.7]} scale={[1.2, 0.5, 0.8]}><sphereGeometry args={[headR * 0.4, 10, 8]} />{hairMat}</mesh>
        <mesh position={[headR * 0.7, headR * 1.8, headR * 0.45]} rotation={[0.6, 0.4, -1.0]} scale={[1.1, 0.45, 0.8]}><sphereGeometry args={[headR * 0.36, 10, 8]} />{hairMat}</mesh>
        <mesh position={[headR * 0.3, headR * 2.55, -headR * 0.1]} rotation={[0.2, 0, -0.9]} scale={[1.4, 0.55, 0.9]}><sphereGeometry args={[headR * 0.4, 10, 8]} />{hairMat}</mesh>
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE DISPATCH — a bespoke body where one exists, the parametric person
// everywhere else. `ctl` is the same {facing, speed} either way.
// ═══════════════════════════════════════════════════════════════════
// V'ael wears her SCULPTED body (public/models/vael.glb, vaelModel3D.jsx);
// the painted VaelBody above is the stand-in while it loads or if it fails.
// THE FAIL-SAFE LAW (sculptGate.jsx): each one also yields to its painted body,
// automatically, if the Plaza's frame rate genuinely can't keep up.
const VaelWithFallback = failSafe(VaelSculpted, VaelBody, VAEL_H);
// Lyraei — and the female player, who IS Lyraei in Terror (caP'n) — wear the
// sculpted body too; the painted LyraeiBody stands in while it loads.
const LyraeiWithFallback = failSafe(LyraeiSculpted, LyraeiBody, LYRAEI_H);
// the male player IS Laostre in Terror (caP'n); the painted Aenarchist body stands in while he loads
const LaostreWithFallback = failSafe(LaostreSculpted, AenarchistMBody, AEN_M_H);
// Willie wears his sculpted body; the painted WillyBody (bench, wire and gem) is the fail-safe
const WillyWithFallback = failSafe(WillieSculpted, WillyBody, WILLY_H);
// the FORGE's Willie keeps the frozen 2026-09-16 sculpt (caP'n, 2026-09-23) — same painted fail-safe
const WillyForgeWithFallback = failSafe(WillieForgeSculpted, WillyBody, WILLY_H);
// V'yen wears her sculpted body; the painted VyenBody (ledger and pen) is the fail-safe
const VyenWithFallback = failSafe(VyenSculpted, VyenBody, VYEN_H);
// Venus wears her sculpted body; the painted VenusBody (two packs held up) is the fail-safe
const VenusWithFallback = failSafe(VenusSculpted, VenusBody, VENUS_H);
const BESPOKE = { vael: VaelWithFallback, lyraei: LyraeiWithFallback, player_f: LyraeiWithFallback, willy: WillyWithFallback, willy_forge: WillyForgeWithFallback, vyen: VyenWithFallback, venus: VenusWithFallback, player_m: LaostreWithFallback };
export function FolkBody({ id, ctl }) {
  const B = BESPOKE[id];
  if (B) return <B ctl={ctl} />;
  const spec = personSpec(id);
  if (!spec) return null;
  return <PersonBody spec={spec} ctl={ctl} />;
}
const FOLK_H = { vael: VAEL_H, lyraei: LYRAEI_H, player_f: LYRAEI_H, willy: WILLY_H, willy_forge: WILLY_H, vyen: VYEN_H, venus: VENUS_H, player_m: AEN_M_H };
export const folkHeight = (id) => FOLK_H[id] ?? (personSpec(id)?.h ?? PERSON_H);
export const folkIdFor = (gender) => (gender === "f" ? "player_f" : "player_m");
// the picture each body is translated from — the review room shows it
export const FOLK_REFERENCE = {
  vael: "vael.webp", willy: "willy.webp",
  lyraei: "player_f_down_a.webp", player_f: "player_f_down_a.webp", player_m: "player_m_down_a.webp",
  vyen: "plaza_1.webp", venus: "plaza_1.webp",
};
// where in `plaza_1.webp` (2000×2000) each painted-in person lives — [x, y, w, h] in fractions
export const FOLK_CROP = {
  vyen: [0.63, 0.27, 0.34, 0.2], venus: [0.01, 0.69, 0.2, 0.21], willy_bench: [0.16, 0.27, 0.2, 0.15],
};
